import { useEffect, useState } from 'react'
import { naira, lagosToday, lagosDaysAgo, lagosTime, nightsBetween, partPaidNote, roomChargeDept, seesStayTimes, cyclesFor, friendlyStayError, addDays } from '../lib/format'
import { billedToLabel, checkOutStay, loadFreeRooms, loadStayMoves, moveStayRoom, undoLastRoomMove, loadCustomers, linkCustomerToGuest, recordStayCredit, checkRepeatPayment, deleteOrderItem, deleteStay, extendStayTo, loadBranchStaySettings, loadFolio, moveRoomChargeDate, recordStayPayment, reopenStay, saveRepayment, searchSimilarGuests, settleBilledToStays, unlinkCustomerFromGuest, updateOrderItem, updateOverstayFee, updateStayBillTo, updateStayDetails } from '../lib/data'
import { useToast } from '../components/Toast'
import PaymentMethodPicker, { paymentParts, paymentAllocated } from './PaymentMethodPicker'
import FolioStatement from './FolioStatement'
import { MANAGEMENT, SUPERVISOR, is } from '../lib/roles'
import { tracked } from '../lib/loadfail'

// Was named SUPERVISOR_ROLES but has always been manager/gm/admin —
// NOT the gm/admin set that is_supervisor() means.
const SUPERVISOR_ROLES = MANAGEMENT

// A date as the desk says it out loud: "Fri 16 Oct". Noon so the
// timezone can never roll it back a day.
const dShort = d => d
  ? new Date(String(d).slice(0, 10) + 'T12:00:00')
      .toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })
  : ''

export default function Folio({ boot, room, onClose, onChanged }) {
  const { staff, allLocations, locations } = boot
  const payMethods = ['pos', 'cash']   // deliberately fixed, not derived from branch config —
                                        // Rooms payment is POS/Cash only, no Transfer
  const toast = useToast()
  const [data, setData] = useState(null)
  const [branchSettings, setBranchSettings] = useState(null)
  // The payment DATE, not just today: a guest who checks in one day and
  // pays the next should have the payment dated when it was taken, so the
  // day's reception figures are right. Defaults to today.
  // method starts null, deliberately. It used to start on 'pos', and a
  // payment recorded without anyone touching the chips went down as POS
  // regardless — the same fault found on the sales screen on 5 November
  // 2026, where four months of takings read 75-96% POS at every bar and
  // measured how rarely the default was changed rather than how guests
  // paid. Reception handles the larger sums, so it matters more here.
  const [pay, setPay] = useState({ amount: '', method: null, split: null, date: lagosToday() })
  // Putting the bill on credit (567). A guest who leaves owing was only
  // ever an implicit balance on a banner; this moves the debt onto a
  // named account, where Credit and Recovery already know how to chase
  // it. Manager/GM/admin only — turning money due into a debt is not a
  // front-desk decision, and if nobody senior is there the bill simply
  // stays outstanding, which is what happens today and is safe.
  const [creditOpen, setCreditOpen] = useState(false)
  const [creditDraft, setCreditDraft] = useState(null)
  // Repointing or clearing a department credit account's guest link
  // (5 November 2026). The link could only ever be CREATED in the app;
  // undoing a wrong one meant a database migration.
  const [linkFix, setLinkFix] = useState(null)   // { customerId, customerName }
  const [linkQuery, setLinkQuery] = useState('')
  const [linkResults, setLinkResults] = useState([])
  // Settling every room billed to this guest in one payment (578).
  const [groupOpen, setGroupOpen] = useState(false)
  const [groupDraft, setGroupDraft] = useState(null)
  const [accounts, setAccounts] = useState(null)
  const [accountSearch, setAccountSearch] = useState('')
  // Correcting the date of a charge already on the folio — GM/admin only
  // (migration 292). Previously impossible: Corrections covers sales and
  // stock movements, not room charges, so every mistake needed SQL.
  const [movingCharge, setMovingCharge] = useState(null)
  const [isOverstay, setIsOverstay] = useState(false)
  // Set when a reopened stay is being checked out again and we have the
  // date it originally left on, so the person can confirm rather than
  // today's date being stamped on silently (migration 459).
  const [checkoutDate, setCheckoutDate] = useState(null)
  // Moving a guest to another room, mid-stay (576). `moving` holds the
  // open sheet; `moves` is what this bill already carries from earlier
  // moves, so the folio can show it alongside the other charges.
  const [moving, setMoving] = useState(null)
  const [undoing, setUndoing] = useState(null)   // the move pending confirmation
  // Making a move is front-desk work; reversing a charge is a manager's.
  const canManage = is(staff.role, MANAGEMENT)
  const [freeRooms, setFreeRooms] = useState(null)
  const [moves, setMoves] = useState([])
  const [movesKey, setMovesKey] = useState(0)
  useEffect(() => {
    if (!room?.stay_id) { setMoves([]); return }
    tracked('Room moves on this stay', loadStayMoves(room.stay_id))
      .then(setMoves).catch(() => setMoves([]))
  }, [room?.stay_id, movesKey])
  // Money that went through ANOTHER department's POS/cash (e.g. a MainBar bill
  // charged to the room, then paid on the bar's terminal). Empty = Reception.
  // Which PART of the bill a payment clears — the room, one department, or
  // (empty, the default) the bill as a whole (migration 496).
  // Which charges the guest is paying for right now (migration 505).
  // Empty = against the whole bill, exactly as every payment behaved
  // before ticking existed.
  const [picked, setPicked] = useState({})
  const [editing, setEditing] = useState(null)   // { dailyRate, billingCycle, scheduledOut, rateReason } while open
  const [billToSuggestions, setBillToSuggestions] = useState([])
  const [billToOpen, setBillToOpen] = useState(false)
  const [overstayDraft, setOverstayDraft] = useState(null)   // amount string while editing
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [editingLine, setEditingLine] = useState(null)   // the order line being edited
  const [lineDraft, setLineDraft] = useState(null)
  const [deletingLine, setDeletingLine] = useState(null)  // the order line pending delete confirmation
  const [printing, setPrinting] = useState(false)
  const [busy, setBusy] = useState(false)
  // Set once the leaving date has been moved from here, so the panel
  // goes away immediately instead of waiting for the folio to reload.
  const [extended, setExtended] = useState(false)

  const refresh = () => { tracked("This guest's bill", loadFolio(room.stay_id)).then(setData).catch(() => setData(null)) }
  useEffect(refresh, [room.stay_id])
  useEffect(() => {
    tracked("The branch's stay settings", loadBranchStaySettings(staff.branch_id)).then(setBranchSettings).catch(() => setBranchSettings(null))
  }, [staff.branch_id])
  useEffect(() => {
    if (!billToOpen) return
    const t = setTimeout(() => {
      searchSimilarGuests(staff.branch_id, editing?.billTo || '').then(setBillToSuggestions)
    }, 400)
    return () => clearTimeout(t)
  }, [editing?.billTo, staff.branch_id, billToOpen])

  useEffect(() => {
    if (!linkFix) return
    const t = setTimeout(() => {
      searchSimilarGuests(staff.branch_id, linkQuery).then(setLinkResults).catch(() => setLinkResults([]))
    }, 400)
    return () => clearTimeout(t)
  }, [linkQuery, staff.branch_id, linkFix])

  if (!data) return (
    <div className="fixed inset-0 z-50 bg-bg p-5">
      <button onClick={onClose} className="text-dim">Close</button>
      <p className="mt-4 text-dim">Loading…</p>
    </div>
  )

  const { orders, payments, folio, stay, departmentCredit, billedToYou, paidByItem, receiptByItem } = data
  const outstanding = Number(folio?.outstanding ?? 0)
  // The room's own balance — used for the pay-button default and
  // checkout logic below, which are genuinely room-specific
  // operations (a room payment can't settle a separate department's
  // credit ledger, so those stay scoped to the room alone).
  const departmentCreditTotal = (departmentCredit || []).reduce((s, d) => s + Number(d.balance), 0)
  const billedToYouTotal = (billedToYou || []).reduce((s, b) => s + Number(b.outstanding), 0)
  // What's actually shown as "Outstanding" — per explicit correction,
  // linked department credit AND other stays billed to this guest
  // both belong in this headline figure, not just displayed
  // separately alongside it.
  const totalOutstanding = outstanding + departmentCreditTotal + billedToYouTotal
  const live = ['reserved', 'occupied'].includes(room.status) && !!room.stay_id

  // Room-rate countdown — how much of the booked period has elapsed
  // vs remains, purely time-based against the planned nights. This is
  // deliberately independent of payment/outstanding: an advance that
  // exactly matches the room rate for the period gets fully "used up"
  // by the last night regardless of what's been charged to other
  // departments in the meantime (see Alphonso — his room rate is fully
  // covered even though his overall balance owes money for extras).
  const totalNights = Number(folio?.nights ?? 0)
  const dailyRate = Number(folio?.daily_rate ?? 0)
  const checkIn = folio?.check_in_date || room.check_in_date
  const scheduledOut = folio?.scheduled_out || room.scheduled_out
  const rawNightsElapsed = checkIn
    ? Math.round((new Date(lagosToday()) - new Date(checkIn)) / 864e5) : 0
  const nightsElapsed = Math.max(0, Math.min(totalNights, rawNightsElapsed))
  const nightsLeft = Math.max(0, totalNights - nightsElapsed)
  const consumedRoomCharge = nightsElapsed * dailyRate
  const remainingRoomCharge = Math.max(0, Number(folio?.room_charge ?? 0) - consumedRoomCharge)
  const pctElapsed = totalNights > 0 ? Math.min(100, Math.round((nightsElapsed / totalNights) * 100)) : 0
  const showCountdown = room.status === 'occupied' && totalNights > 1

  const orderLines = orders.flatMap(o => (o.order_items || [])
    .map(li => ({ ...li, date: o.business_date, servedBy: o.served_by,
                  servedName: o.served?.full_name || '',
                  orderId: o.id, settlement: o.settlement,
                  approval: o.approval_status || 'approved', decisionNote: o.decision_note })))
  // Which lines the guest is actually BILLED for. This must match
  // v_stay_folio.orders_charge exactly:
  //   settlement = 'charged_to_room' AND order_type <> 'pr_damage'
  // Without both filters the invoice totalled complimentary/damage
  // items and orders already paid for at the department, so it came
  // out higher than the outstanding figure the app showed everywhere
  // else. Non-billable lines are still listed on screen (below), just
  // never added to money.
  // Must match v_stay_folio.orders_charge EXACTLY, which since 248 also
  // requires approval_status = 'approved'. Without this third condition
  // a pending charge would print on the invoice while being absent from
  // its total — the "lines don't add up" bug fixed once already.
  //
  // And STAFF orders. The view excludes both pr_damage AND staff:
  //   oi.order_type <> all (array['pr_damage', 'staff'])
  // This filter only ever excluded pr_damage, so a staff meal charged to
  // a room was listed as a charge on the guest's invoice and counted in
  // the folio's own breakdown, while being absent from the total the
  // app bills. The same bug the comment above was written to prevent,
  // surviving in the line list instead of the total. A staff order is
  // consumed and never billed to the guest, so it belongs with the
  // complimentary lines.
  const isBillable = li => li.settlement === 'charged_to_room'
    && li.order_type !== 'pr_damage' && li.order_type !== 'staff'
    && li.approval === 'approved'
  const billableLines = orderLines.filter(isBillable)
  const freeLines = orderLines.filter(
    li => li.settlement === 'charged_to_room'
       && (li.order_type === 'pr_damage' || li.order_type === 'staff')
       && li.approval === 'approved')
  // Awaiting front desk: shown on screen, never billed until approved.
  const pendingLines = orderLines.filter(
    li => li.settlement === 'charged_to_room' && li.approval === 'pending')
  const payParts = paymentParts(pay, pay.amount)
  const payAllocated = paymentAllocated(pay, pay.amount)
  // Choosing Split counts: the split amounts are checked against the
  // total separately, as they always were.
  const payMethodChosen = !!pay.split || !!pay.method

  // ── What the bill is made of, department by department ──────────────
  //
  // One room bill is really several: the room itself, plus whatever each
  // department charged to it. A guest settling the restaurant and leaving
  // the bar for later needs those kept apart (migration 496).
  //
  // The parts are built from the SAME billableLines and folio figures the
  // total already uses, so they cannot drift from it — the "lines don't
  // add up" bug is only ever caused by a second, parallel calculation.
  // Money with no department named is shown on its own rather than being
  // spread across the parts on the guest's behalf.
  //
  // A typed charge carries no location_id (the stock trigger must not
  // fire on a line with no stock item), so those are grouped together and
  // labelled for what they are rather than silently assigned a department.
  // Computed plainly, NOT with useMemo. Everything from here down runs
  // after the `if (!data) return` guard above, so a hook placed here would
  // be called on the loaded render and skipped on the loading one — React
  // throws "rendered more hooks than during the previous render" the moment
  // the folio arrives, which is a blank screen. A few lines and payments
  // cost nothing to add up on every render.
  const locName = (id) => (allLocations || locations || []).find(l => l.id === id)?.name
  const tickedOn = (k) => Number((paidByItem || {})[k] || 0)

  // ── What is actually still unpaid, charge by charge ─────────────────
  //
  // Two kinds of money land on a folio. Money TICKED against a charge
  // settles that charge exactly. Money paid against the bill as a whole
  // — which is most of it, and every payment taken before ticking
  // existed — settles no particular charge, so without a rule it
  // settles none of them: Mr Ngoka had paid ₦184,400 of ₦187,900 and
  // every single line still showed as owing, with the picker offering
  // him the whole bill again.
  //
  // So untagged money covers the bill in the order the guest incurred
  // it: the room first, then charges oldest first. It is stated here
  // once and used by BOTH the breakdown and the picker, so the two can
  // never say different things about the same line.
  const covered = (() => {
    const out = {}
    const roomFull0 = Number(folio?.room_charge || 0) + Number(folio?.overstay_charge || 0)
    // Everything payable on this folio, in the order money should reach it.
    const inOrder = [{ key: 'room', full: roomFull0 }]
      .concat([...billableLines]
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))
                     || String(a.id).localeCompare(String(b.id)))
        .map(li => ({ key: li.id, full: Number(li.amount || 0) })))

    // Money with no charge named: everything paid, less what was ticked.
    let pot = Number(folio?.total_paid || 0)
      - Object.values(paidByItem || {}).reduce((t, v) => t + Number(v || 0), 0)

    for (const x of inOrder) {
      const owed = Math.max(0, x.full - tickedOn(x.key))
      const take = Math.max(0, Math.min(pot, owed))
      pot -= take
      out[x.key] = take
    }
    return out
  })()
  // What is left on one charge: its value, less money ticked against it,
  // less its share of the untagged money.
  const stillOwed = (key, full) =>
    Math.max(0, Number(full || 0) - tickedOn(key) - Number(covered[key] || 0))
  const billParts = (() => {
    const roomCharged = Number(folio?.room_charge || 0) + Number(folio?.overstay_charge || 0)
    const byDept = new Map()
    for (const li of billableLines) {
      const key = li.location_id || 'typed'
      const prev = byDept.get(key) || { charged: 0, left: 0 }
      prev.charged += Number(li.amount || 0)
      prev.left += stillOwed(li.id, li.amount)
      byDept.set(key, prev)
    }

    const parts = []
    if (roomCharged > 0) {
      const l = stillOwed('room', roomCharged)
      parts.push({ key: 'room', applied: 'room', locationId: null, name: 'Room charge',
                   charged: roomCharged, paid: roomCharged - l, left: l })
    }
    for (const [key, agg] of byDept) {
      const charged = agg.charged
      const paid = charged - agg.left
      parts.push({
        key,
        applied: key === 'typed' ? null : 'department',
        locationId: key === 'typed' ? null : key,
        // Charges that carry no department: everything typed before this
        // change, plus anything typed at Reception itself, which genuinely
        // belongs to no department. Can't be settled on its own — there is
        // nothing to settle it against — so it is left off the picker and
        // cleared by paying the bill as a whole.
        name: key === 'typed' ? 'Other charges — no department' : (locName(key) || 'A department'),
        charged, paid, left: agg.left,
      })
    }
    // Department credit — a different ledger, offered here all the same.
    // A guest standing at the desk wanting to clear the restaurant does
    // not care which table it lives in, and leaving the desk no way to
    // take that money is how Mr Alphonso's ₦4,500 ended up on his room
    // while his restaurant bill stayed exactly ₦4,500 unpaid.
    //
    // Only offered when we know which account holds it. Without that the
    // line still SHOWS below, it just cannot be paid from here.
    for (const d of (departmentCredit || [])) {
      const bal = Number(d.balance || 0)
      if (bal <= 0.009) continue
      parts.push({
        key: `credit:${d.location_id}`,
        applied: d.customerId ? 'credit' : null,
        locationId: d.location_id,
        customerId: d.customerId || null,
        name: `${d.location_name} — on Credit`,
        charged: bal, paid: 0, left: bal,
        onCredit: true,
      })
    }
    return parts
  })()

  // Money that named what it was for: ticked against specific charges
  // (505), or marked for a department (496). New payments only ever do
  // the first, so the two cannot double-count.
  const earmarked = Object.values(paidByItem || {}).reduce((t, v) => t + Number(v || 0), 0)
    + payments.filter(p => p.applied_to).reduce((t, p) => t + Number(p.amount || 0), 0)
  const generalPaid = Number(folio?.total_paid || 0) - earmarked
  // Only worth showing the split when the bill genuinely has more than one
  // part. A room with no orders on it does not need a breakdown.
  const showParts = billParts.length > 1 || departmentCreditTotal > 0 || billedToYouTotal > 0

  // ── What the guest can pay for, one tickable thing per line ─────────
  //
  // "I'll pay for the two waters" and "just last night's food" are the
  // questions the desk is actually asked, and neither could be answered
  // before: a payment was an amount against a stay and nothing else, so
  // every screen had to guess what it settled. Ticking records it.
  //
  // Each entry carries what is LEFT on it, so a charge already ticked
  // off drops out and a part-paid one shows only the remainder.
  const payables = []
  const roomFull = Number(folio?.room_charge || 0) + Number(folio?.overstay_charge || 0)
  const roomLeft = stillOwed('room', roomFull)
  // The room, night by night. A guest on a long stay settles some nights
  // and leaves the rest, so each unpaid night is its own tickable line
  // rather than the room being one all-or-nothing lump.
  //
  // Nights already covered drop off the top: whatever has been ticked
  // against the room buys whole nights at the stay's own rate, and only
  // what is left is offered. The LAST night carries any remainder — an
  // over-stay fee, or a rate that changed mid-stay — so the night prices
  // always add back up to the room charge exactly.
  const nightRate = Number(folio?.daily_rate || 0)
  const totalNightsOnBill = Number(folio?.nights || 0)
  const nightsCovered = nightRate > 0
    ? Math.floor((tickedOn('room') + Number(covered.room || 0)) / nightRate) : 0
  const nightsToOffer = Math.max(0, totalNightsOnBill - nightsCovered)

  // ── Has he paid past his leaving date? ──────────────────────────────
  //
  // Nothing in the app has ever moved scheduled_out. A guest pays for
  // another night, the money lands correctly, the bill is right — and
  // the board still shows the date he was originally due to leave, so
  // the room reads "Past due" and "Settled" at the same time. On 16
  // October that was three of the four past-due rooms at Awka.
  //
  // The room money buys whole nights at this stay's own rate, so where
  // those nights reach past the leaving date, the date is simply behind
  // and the desk is asked — here, with the guest still in front of them
  // — whether to move it. Asked, not done: money can also be an old
  // bill being settled by someone who is not staying on.
  const paidThroughDate = (nightRate > 0 && (folio?.check_in_date || room.check_in_date))
    ? addDays(folio?.check_in_date || room.check_in_date, nightsCovered) : null
  const dateIsBehind = !!paidThroughDate && !!folio?.scheduled_out
    && paidThroughDate > folio.scheduled_out
    && room.status !== 'checked_out' && !extended
  if (roomLeft > 0.009 && nightsToOffer > 1 && nightRate > 0) {
    const inDate = folio?.check_in_date || room.check_in_date
    for (let i = 0; i < nightsToOffer; i++) {
      const n = nightsCovered + i
      const isLast = i === nightsToOffer - 1
      const amt = isLast ? Math.max(0, roomLeft - nightRate * (nightsToOffer - 1)) : nightRate
      if (amt <= 0.009) continue
      const d = inDate ? new Date(new Date(inDate).getTime() + n * 864e5) : null
      payables.push({
        key: `room:${n}`, kind: 'room', orderItemId: null,
        day: 'The room',
        what: `Night ${n + 1}`,
        where: d ? d.toISOString().slice(0, 10) : 'Accommodation',
        // The night the money ran out part-way through shows less than
        // the nightly rate, which reads as a wrong price unless it says
        // why. The LAST night can also run HIGHER than the rate when an
        // over-stay fee rides on it — nothing is part-paid there, so
        // the note stays away.
        note: partPaidNote(nightRate, amt),
        amount: Math.round(amt * 100) / 100,
      })
    }
  } else if (roomLeft > 0.009) {
    payables.push({
      key: 'room', kind: 'room', orderItemId: null,
      day: 'The room',
      what: 'Room charge',
      // Once part of the room is paid, describing the WHOLE stay beside
      // what is LEFT reads as arithmetic that does not work: Mr Ngoka's
      // line said "5 nights at ₦35,000" against ₦30,000. So a part-paid
      // room says what has been paid instead, and only an untouched one
      // describes the stay.
      where: partPaidNote(roomFull, roomLeft)
        || (totalNightsOnBill
          ? `${totalNightsOnBill} night${totalNightsOnBill > 1 ? 's' : ''} at ${naira(nightRate)}`
          : 'Accommodation'),
      amount: roomLeft,
    })
  }
  for (const li of billableLines) {
    const l = stillOwed(li.id, li.amount)
    if (l <= 0.009) continue
    payables.push({
      key: li.id, kind: li.category === 'food' ? 'food' : li.category === 'minimart' ? 'shop' : 'drink',
      orderItemId: li.id,
      day: li.date,
      what: `${li.description}${Number(li.qty) > 1 ? ` × ${li.qty}` : ''}`,
      where: locName(li.location_id) || (li.category === 'food' ? 'Restaurant'
        : li.category === 'minimart' ? 'Minimart' : 'Bar'),
      // Mr Ngoka's ₦12,000 pasta had ₦7,000 on it — the ₦35,000 that
      // settled his bill ran out on that line — and the folio offered
      // the ₦5,000 with nothing to say why, so it read as a meal nobody
      // had paid for.
      note: partPaidNote(li.amount, l),
      amount: l,
    })
  }
  // The other ledger. Ticked here, settled there — the desk should not
  // have to know which table a bill lives in.
  for (const d of (departmentCredit || [])) {
    const bal = Number(d.balance || 0)
    if (bal <= 0.009 || !d.customerId) continue
    payables.push({
      key: `credit:${d.location_id}`, kind: 'credit',
      orderItemId: null, customerId: d.customerId, locationId: d.location_id,
      day: 'On credit',
      what: `${d.location_name} account`,
      where: 'Settles on Credit, not on this bill',
      amount: bal,
    })
  }

  // Newest day first, the room always at the top.
  // payDays, billAll, billTotal and billLeft lived here. They grouped
  // and totalled the old tick list; the bill is now drawn from
  // billableLines and the v_stay_folio figures, which is what the
  // printed invoice uses too, so the screen and the paper cannot
  // disagree in front of a guest (20 October 2026).

  const pickedList = payables.filter(p => picked[p.key])
  // The ticking is gone (20 October 2026): the front desk no longer
  // decides what a payment settles, so there is nothing to tick and
  // nothing to fill in from it. picked stays as an empty object because
  // the save path still reads it.

  const cycles = cyclesFor(branchSettings?.allowedCycles)
  const overstayDefault = branchSettings?.overstayDefault ?? null
  const currentOverstay = stay?.overstay_fee != null ? Number(stay.overstay_fee) : null
  // Setting exactly the branch default is open to anyone (the trigger
  // only restricts a DIFFERENT amount or removing one) — matches
  // enforce_overstay_fee exactly, confirmed against its real body
  // rather than assumed.
  // Matches is_supervisor() exactly (confirmed against its real body):
  // role in ('gm', 'admin') — NOT manager, a narrower set than the
  // manager/gm/admin group that governs undoing an old checkout.
  const canSetNonDefaultOverstay = is(staff.role, SUPERVISOR)
  // Arrival/departure clock times: oversight roles only.
  const showTimes = seesStayTimes(staff.role)

  function openCredit() {
    setCreditDraft({
      amount: String(outstanding > 0 ? Number(outstanding.toFixed(2)) : ''),
      customerId: '',
      // Brackets, because a bare room number in an account name is
      // refused by the name rule — a room is not a person, and the
      // room changes.
      name: `${room.guest_name || 'Guest'}${room.room_number ? ` (${room.room_number})` : ''}`,
      note: '',
    })
    setCreditOpen(true)
    if (accounts === null) {
      tracked('Customer accounts', loadCustomers(staff.branch_id))
        .then(setAccounts).catch(() => setAccounts([]))
    }
  }

  // Oldest booking first, matching the function's own ORDER BY exactly —
  // billedToYou arrives newest-first, so it must be re-sorted or the
  // preview would promise one room and the database pay another.
  const groupRooms = (billedToYou || [])
    .filter(b => Number(b.outstanding) > 0.009)
    .slice()
    .sort((a, b) => String(a.check_in_date || '').localeCompare(String(b.check_in_date || ''))
      || String(a.room_number || '').localeCompare(String(b.room_number || '')))

  const groupSpread = (() => {
    let pot = Math.round(Number(groupDraft?.amount || 0) * 100) / 100
    const out = []
    for (const b of groupRooms) {
      if (pot <= 0.009) break
      const take = Math.min(pot, Math.round(Number(b.outstanding) * 100) / 100)
      pot = Math.round((pot - take) * 100) / 100
      out.push({ ...b, take })
    }
    return out
  })()

  async function saveGroup() {
    const amt = Number(groupDraft?.amount)
    if (!(amt > 0)) { toast('Enter how much was paid.', 'error'); return }
    setBusy(true)
    try {
      const r = await settleBilledToStays({
        payerStayId: room.stay_id, amount: amt,
        method: groupDraft.method, date: groupDraft.date || lagosToday(),
        note: groupDraft.note?.trim() || null,
      })
      const n = (r?.rooms || []).length
      toast(`${naira(r.applied)} settled across ${n} room${n === 1 ? '' : 's'}`
        + (Number(r.still_owed) > 0.009 ? ` · ${naira(r.still_owed)} still owing` : ''), 'success')
      setGroupOpen(false); setGroupDraft(null); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  async function saveCredit() {
    const amt = Number(creditDraft?.amount)
    if (!(amt > 0)) { toast('Enter how much is going on credit.', 'error'); return }
    if (!creditDraft.customerId && !creditDraft.name.trim()) {
      toast('Say whose account this debt goes on.', 'error'); return
    }
    setBusy(true)
    try {
      const r = await recordStayCredit({
        stayId: room.stay_id, amount: amt,
        customerId: creditDraft.customerId || null,
        customerName: creditDraft.name.trim(),
        note: creditDraft.note.trim() || null,
      })
      toast(`${naira(r.amount)} now owed by ${r.customer}`, 'success')
      setCreditOpen(false); setCreditDraft(null); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  async function submitPayment() {
    if (payAllocated <= 0) return
    // Taking more than the bill is how money ends up on the wrong folio: the
    // guest's note covers two rooms, or a figure is typed with an extra zero.
    // It is allowed — a deposit against nights still to come is normal — but
    // it is confirmed first, with the numbers in front of the person.
    const owing = Math.round(totalOutstanding * 100) / 100
    if (owing > 0.009 && payAllocated - owing > 0.009 &&
        !window.confirm(`This is ${naira(payAllocated)} against ${naira(owing)} still owing — `
          + `${naira(payAllocated - owing)} more than the bill.\n\n`
          + 'Save it anyway? Tap Cancel if it belongs on another room or another stay.')) {
      return
    }
    if (owing <= 0.009 &&
        !window.confirm(`This bill is already settled. Save ${naira(payAllocated)} on top?\n\n`
          + 'Tap Cancel if the money belongs on another room or another stay.')) {
      return
    }
    // Asked of the database, not the screen — the screen is exactly what
    // is stale when somebody re-enters a payment they could not see land.
    // Skipped when only credit accounts were ticked: that money goes to
    // another table entirely, so this would read the wrong ledger.
    const creditPicks = pickedList.filter(p => p.kind === 'credit')
    const folioPicks = pickedList.filter(p => p.kind !== 'credit')
    const onlyCredit = creditPicks.length > 0 && folioPicks.length === 0

    const repeat = onlyCredit ? null : await checkRepeatPayment(room.stay_id, payAllocated)
    if (repeat && !window.confirm(
          `${naira(repeat.amount)} was already recorded on this bill `
          + `${repeat.minutesAgo} minute${repeat.minutesAgo > 1 ? 's' : ''} ago`
          + `${repeat.who ? ` by ${repeat.who}` : ''}.\n\n`
          + `Is this a second ${naira(payAllocated)} the guest has just paid?\n\n`
          + 'Tap Cancel if it is the same money — it is already saved.')) {
      return
    }
    setBusy(true)
    try {
      // The money covers the ticked lines in the order they are shown,
      // so a part payment lands on the earliest charges rather than
      // being smeared across everything. Whole-line in the ordinary
      // case; the remainder only ever falls on the last line reached.
      let pot = payAllocated
      const creditShare = []
      const itemShare = []
      // Nothing ticked? Then the app allocates it the same way the folio
      // already shows untagged money settling the bill: the room first,
      // then charges oldest first.
      //
      // Not a nicety. A department's daily figures are built from what a
      // payment settled, so a payment that names nothing reaches no
      // department at all — ₦4,900 of Mr Ngoka's never got to MainBar
      // because the desk, quite reasonably, just typed the amount. Making
      // the department's money depend on a new habit was the wrong design;
      // the app works it out and the desk corrects it by ticking.
      //
      // Credit accounts are left out: that is another ledger, and moving
      // money into it is a decision, never a default.
      // THE FRONT DESK SETTLES THE ROOM. NOTHING ELSE. (20 October 2026)
      //
      // This used to spread an untagged payment across the room AND
      // every department charge, oldest first — which is why a clerk who
      // ticked nothing still "allocated" MainBar's money, and why
      // Reception appeared to be recording payments on departments'
      // behalf in the live test.
      //
      // The reasoning behind the old behaviour was sound at the time: a
      // payment that named nothing reached no department at all, and
      // ₦4,900 of Mr Ngoka's never got to MainBar. The answer then was
      // to let the app guess. The answer now is better — the department
      // records what it was physically handed, so there is nothing left
      // to guess and nothing for the front desk to get wrong.
      //
      // The guest's bill still clears in full: outstanding counts every
      // payment on the stay however it was allocated. The department
      // charges simply sit as "paid by the guest, not yet recorded
      // here" until the department claims them.
      const toSettle = [...payables].filter(p => p.kind === 'room')
      for (const p of toSettle) {
        if (pot <= 0.009) break
        const take = Math.min(pot, p.amount)
        pot -= take
        if (p.kind === 'credit') creditShare.push({ pick: p, amount: take })
        else itemShare.push({ orderItemId: p.orderItemId, amount: take })
      }
      // Nights are shown separately but settle one pot — the room charge
      // — so they are recorded as one row rather than several identical
      // ones with no order line behind them.
      const roomTotal = itemShare.filter(i => !i.orderItemId)
        .reduce((t, i) => t + i.amount, 0)
      const merged = itemShare.filter(i => i.orderItemId)
      if (roomTotal > 0.009) merged.unshift({ orderItemId: null, amount: Math.round(roomTotal * 100) / 100 })

      // A department's credit lives in credit_repayments against a
      // customer account, not in payments against the stay. Writing it to
      // the stay would clear the wrong debt and leave the real one
      // standing — so it goes to the other ledger, the same way the
      // Credit page does it.
      for (const c of creditShare) {
        await saveRepayment({
          staff, customerId: c.pick.customerId,
          amount: c.amount, method: payParts[0]?.method || 'pos',
          paidOn: pay.date || lagosToday(),
          locationId: c.pick.locationId,
          note: `Taken at Reception, Room ${room.room_number}`,
        })
      }

      // Everything else is a payment on the stay. Ticked lines ride with
      // it so what it settled is recorded rather than inferred later.
      const onFolio = payAllocated - creditShare.reduce((t, c) => t + c.amount, 0)
      if (onFolio > 0.009) {
        const scale = onFolio / payAllocated
        await recordStayPayment({
          staff, stayId: room.stay_id, businessDate: pay.date || lagosToday(),
          cycle: folio?.billing_cycle,
          parts: payParts.map(p => ({ ...p, amount: Number((Number(p.amount) * scale).toFixed(2)) }))
            .filter(p => p.amount > 0),
          // Always Reception: a room bill is settled at the front desk.
          // See the note where the department picker used to be.
          isOverstay, locationId: null,
          items: merged,
        })
      }

      toast(pickedList.length
        ? `Payment recorded against ${pickedList.length} line${pickedList.length === 1 ? '' : 's'}`
        : `Payment recorded — put against ${merged.length} charge${merged.length === 1 ? '' : 's'}, oldest first`,
        'success')
      setPay({ amount: '', method: null, split: null, date: lagosToday() }); setIsOverstay(false)
      setPicked({})
      refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }


  // Checking out used to stamp today, full stop. On a stay that had been
  // reopened to fix a charge, that quietly moved the departure date and
  // charged the nights in between — it caught four stays in two days. If
  // the stay remembers when it really left, ask instead of assuming.
  // Always ask which day they left (Chris, 26 October 2026: "staff forgot
  // to check out a guest, and it added an extra night").
  //
  // Until now the date was only ever asked for on a stay that had been
  // REOPENED; every other check-out went straight through on today's
  // date. So a guest who left on Friday and was checked out on Monday was
  // billed for the weekend, and the only way to put it right was to check
  // out wrongly, undo it, and check out again — which is the one path
  // that showed the date field. That is why the undo-and-redo habit
  // exists, and it is a workaround for this.
  //
  // Defaulting to today keeps the common case one extra tap, and the
  // sheet below shows what the chosen day bills before anything is saved.
  function doCheckOut() {
    setCheckoutDate(stay?.pre_reopen_actual_out || lagosToday())
  }

  // Which rooms this guest could move into. The database constraint
  // guards a stay's WHOLE date range, so a room free only for the nights
  // remaining would be refused at the moment of saving — better to not
  // offer it. Their own stay is excluded, or it would block every room
  // they are already in.
  async function openMove() {
    const end = scheduledOut || room.scheduled_out
    setMoving({ toRoomId: '', on: lagosToday(), kind: 'fault', reason: '',
                override: '', outOfService: false })
    setFreeRooms(null)
    try {
      const rs = await loadFreeRooms(staff.branch_id, checkIn, end, room.stay_id)
      setFreeRooms(rs.filter(r => r.id !== room.room_id))
    } catch (e) { setFreeRooms([]); toast(friendlyStayError(e) || e.message, 'error') }
  }

  async function doUndoMove() {
    setBusy(true)
    try {
      const res = await undoLastRoomMove(room.stay_id, undoing.reason)
      const removed = Number(res?.charge_removed || 0)
      toast(`Back in Room ${res.back_in}`
        + (Math.abs(removed) > 0.009
            ? ` · ${removed > 0 ? naira(removed) + ' taken off' : naira(-removed) + ' put back'}`
            : ''), 'success')
      setUndoing(null); setMovesKey(k => k + 1); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e) || e.message, 'error') }
    setBusy(false)
  }

  async function saveMove() {
    setBusy(true)
    try {
      const res = await moveStayRoom({
        stayId: room.stay_id, toRoomId: moving.toRoomId, movedOn: moving.on,
        kind: moving.kind, reason: moving.reason,
        adjustOverride: moving.override, outOfService: moving.outOfService,
      })
      const adj = Number(res?.adjustment || 0)
      toast(adj > 0.009 ? `Moved to Room ${res.to} · ${naira(adj)} added`
            : adj < -0.009 ? `Moved to Room ${res.to} · ${naira(-adj)} refunded`
            : `Moved to Room ${res.to} · no change to the bill`, 'success')
      setMoving(null); setMovesKey(k => k + 1); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e) || e.message, 'error') }
    setBusy(false)
  }

  async function finishCheckOut(onDate) {
    setBusy(true)
    try {
      await checkOutStay(room.stay_id, onDate)
      toast('Checked out', 'success')
      setCheckoutDate(null)
      onChanged?.(); onClose()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  async function doReopen() {
    setBusy(true)
    try {
      await reopenStay(room.stay_id)
      toast('Check-out undone — guest is back in the room', 'success')
      onChanged?.(); onClose()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  async function doDeleteStay() {
    setBusy(true)
    try {
      await deleteStay(room.stay_id)
      toast('Booking deleted', 'success')
      onChanged?.(); onClose()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  function openEditLine(li) {
    setEditingLine(li)
    setLineDraft({ description: li.description, qty: String(li.qty), unitPrice: String(li.unit_price) })
  }

  async function saveEditLine() {
    setBusy(true)
    try {
      await updateOrderItem(editingLine.id, {
        description: lineDraft.description, qty: Number(lineDraft.qty), unit_price: Number(lineDraft.unitPrice),
      })
      toast('Order updated', 'success')
      setEditingLine(null); refresh(); onChanged?.()
    } catch (e) { toast('Not saved: ' + e.message, 'error') }
    setBusy(false)
  }

  async function doDeleteLine() {
    setBusy(true)
    try {
      await deleteOrderItem(deletingLine.id, deletingLine.orderId)
      toast('Order removed', 'success')
      setDeletingLine(null); refresh(); onChanged?.()
    } catch (e) { toast('Not deleted: ' + e.message, 'error') }
    setBusy(false)
  }

  function openEdit() {
    setEditing({
      dailyRate: String(folio?.daily_rate ?? ''),
      billingCycle: folio?.billing_cycle || cycles[0]?.value,
      scheduledOut: folio?.scheduled_out || room.scheduled_out,
      rateReason: '', billTo: stay?.bill_to || '', billToGuestId: stay?.bill_to_guest_id || null,
    })
  }

  async function saveEdit() {
    setBusy(true)
    try {
      // If nothing but "Bill to" changed, save ONLY that. The full save
      // re-sends the rate, cycle and dates, which can fire the
      // rate-adjustment trigger — and linking who a checked-out stay is
      // billed to should never touch the rate. The comparison uses the
      // same expressions openEdit() filled the form with.
      const same = editing.dailyRate === String(folio?.daily_rate ?? '')
        && editing.billingCycle === (folio?.billing_cycle || cycles[0]?.value)
        && editing.scheduledOut === (folio?.scheduled_out || room.scheduled_out)
        && !editing.rateReason
      if (same) {
        await updateStayBillTo({
          stayId: room.stay_id, billTo: editing.billTo.trim(), billToGuestId: editing.billToGuestId,
        })
      } else await updateStayDetails({
        stayId: room.stay_id, dailyRate: Number(editing.dailyRate),
        billingCycle: editing.billingCycle, scheduledOut: editing.scheduledOut,
        rateReason: editing.rateReason, billTo: editing.billTo.trim(), billToGuestId: editing.billToGuestId,
      })
      toast('Stay updated', 'success')
      setEditing(null); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  // Clearing or repointing the link moves no money: the balance stays on
  // the customer account either way. All that changes is whose folio it
  // appears on, and whether Credit chases it against a guest.
  async function applyLinkFix(guest) {
    setBusy(true)
    try {
      if (guest) await linkCustomerToGuest(linkFix.customerId, guest.id)
      else await unlinkCustomerFromGuest(linkFix.customerId)
      toast(guest ? `Now on ${guest.full_name}` : 'Link removed — chase it on Credit', 'success')
      setLinkFix(null); setLinkQuery(''); setLinkResults([])
      refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  async function saveOverstay() {
    setBusy(true)
    try {
      await updateOverstayFee(room.stay_id, overstayDraft === '' ? null : Number(overstayDraft))
      toast('Over-stay charge updated', 'success')
      setOverstayDraft(null); refresh(); onChanged?.()
    } catch (e) { toast(friendlyStayError(e), 'error') }
    setBusy(false)
  }

  return (
    <div className="fixed inset-0 z-50 bg-bg overflow-y-auto">
      <div className="p-5">
        <button onClick={onClose} className="text-dim">Close</button>
        <h2 className="mt-3 text-2xl font-bold">{room.guest_name || 'Guest'}</h2>
        <p className="text-dim mt-1">
          Room {room.room_number} · {room.check_in_date}
          {showTimes && stay?.checked_in_at ? ` at ${lagosTime(stay.checked_in_at)}` : ''}
          {' to '}{folio?.scheduled_out || room.scheduled_out}
          {folio?.nights ? ` · ${folio.nights} night${folio.nights > 1 ? 's' : ''}` : ''}
        </p>
        {stay?.bill_to && (
          <p className="mt-2 inline-block px-3 py-1 rounded-full bg-amber/20 border border-amber text-amber text-sm font-semibold">
            Billed to: {stay.bill_to}
          </p>
        )}
        {/* Pre-app history. Every line is still here and nothing has been
            edited, but the figures came across from the Excel workbook and
            are not reliable — so this stay is left out of the live owing
            and overpaid totals rather than being presented as fact. */}
        {stay?.is_migrated && (
          <div className="mt-2 px-3 py-2 rounded-xl bg-raise border border-line">
            <p className="text-ink text-sm font-semibold">From before the app</p>
            <p className="text-dim text-sm mt-1">
              This stay came across from the Excel sheet. Every line is kept
              on the record, but the figures were not entered here and are
              not treated as accurate, so this bill is left out of the
              branch's owing and overpaid totals.
            </p>
          </div>
        )}
        {!!departmentCredit?.length && (
          <div className="mt-2 px-3 py-2 rounded-xl bg-clay/10 border border-clay">
            <p className="text-clay text-sm font-semibold">Included above — other departments:</p>
            {departmentCredit.map(d => (
              <p key={d.location_id} className="text-clay text-sm">
                {d.location_name}: {naira(d.balance)}
              </p>
            ))}
            <p className="text-dim text-xs mt-1">
              Settled separately on Credit — paying the room balance doesn't clear this.
            </p>
            {/* Until this existed, a department account pointed at the
                wrong guest could only be put right in the database. */}
            {canManage && departmentCredit.find(d => d.customerId) && (
              <button
                onClick={() => {
                  const d = departmentCredit.find(x => x.customerId)
                  setLinkFix({ customerId: d.customerId, customerName: d.customerName })
                  setLinkQuery(''); setLinkResults([])
                }}
                className="mt-2 text-xs text-amber underline">
                Not this guest? Change or remove
              </button>
            )}
          </div>
        )}
        {!!billedToYou?.length && (
          <div className="mt-2 px-3 py-2 rounded-xl bg-clay/10 border border-clay">
            <p className="text-clay text-sm font-semibold">Rooms billed to this guest:</p>
            {billedToYou.map(b => (
              <p key={b.stay_id} className="text-clay text-sm">
                {b.guest_name} (Room {b.room_number || '—'}){b.check_in_date ? ` · ${b.check_in_date}` : ''}:{' '}
                {billedToLabel(b) || naira(b.outstanding)}
              </p>
            ))}
            {billedToYouTotal > 0.009 && (
              <>
                <button
                  onClick={() => {
                    setGroupDraft({
                      amount: String(Math.round(billedToYouTotal * 100) / 100),
                      method: null, date: lagosToday(), note: '',
                    })
                    setGroupOpen(true)
                  }}
                  className="mt-2 w-full h-11 rounded-xl border border-clay text-clay font-semibold">
                  Settle these rooms · {naira(billedToYouTotal)}
                </button>
                <p className="text-dim text-xs mt-1">
                  One payment, spread across the rooms above, oldest booking first.
                  This room's own bill is separate and is paid below.
                </p>
              </>
            )}
          </div>
        )}
        <button onClick={openEdit} className="text-dim text-sm underline mt-1">
          Edit rate, cycle, or dates
        </button>
        <button onClick={() => setPrinting(true)} className="block text-dim text-sm underline mt-1">
          Print guest statement
        </button>
        {is(staff.role, SUPERVISOR) && (
          <button onClick={() => setConfirmingDelete(true)} className="block text-clay text-sm underline mt-1">
            Delete this booking — training records only
          </button>
        )}

        {dateIsBehind && (
          <div className="mt-4 rounded-2xl border-2 border-amber bg-amber/10 p-4">
            <p className="font-bold text-amber">
              {nightsCovered} night{nightsCovered === 1 ? '' : 's'} paid for — the leaving date says {dShort(folio.scheduled_out)}
            </p>
            <p className="text-dim text-sm mt-1">
              {naira(nightsCovered * nightRate)} of room money at {naira(nightRate)} a night runs to{' '}
              <b className="text-ink">{dShort(paidThroughDate)}</b>. Until the date is moved, the board
              shows this room as past due.
            </p>
            <div className="mt-3 flex gap-2">
              <button disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await extendStayTo(room.stay_id, paidThroughDate)
                    setExtended(true)
                    toast(`Leaving date moved to ${dShort(paidThroughDate)}`, 'success')
                    refresh()
                    onChanged?.()
                  } catch (e) { toast(friendlyStayError(e), 'error') }
                  setBusy(false)
                }}
                className="flex-1 h-12 rounded-xl bg-amber text-bg font-bold disabled:opacity-40">
                {busy ? 'Saving…' : `Move it to ${dShort(paidThroughDate)}`}
              </button>
              <button onClick={() => setExtended(true)}
                className="h-12 px-4 rounded-xl border border-line text-dim font-semibold">
                Leave it
              </button>
            </div>
            <p className="text-dim text-xs mt-2">
              Leave it if the guest is settling an old bill rather than staying on.
            </p>
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
          <Row label="Room charge" value={folio?.room_charge} />
          <Row label="Orders" value={folio?.orders_charge} />
          {Number(folio?.overstay_charge) > 0 && <Row label="Over-stay charge" value={folio?.overstay_charge} />}
          {departmentCreditTotal > 0 && <Row label="Other departments" value={departmentCreditTotal} />}
          {billedToYouTotal > 0 && <Row label="Other bills (billed to you)" value={billedToYouTotal} />}
          <Row label="Paid" value={folio?.total_paid} />
          <div className="flex items-baseline justify-between pt-3 mt-3 border-t border-line">
            <span className="font-semibold">{totalOutstanding < 0 ? 'Deposit remaining' : 'Outstanding'}</span>
            <span className={`text-xl font-bold tnum ${totalOutstanding > 0 ? 'text-clay' : 'text-leaf'}`}>
              {naira(Math.abs(totalOutstanding))}
            </span>
          </div>
        </div>

        {/* Where the debt sits. The Outstanding figure above counts THREE
            things: this folio, department credit on a linked customer
            account, and other rooms billed to this guest. A breakdown that
            showed only the first would contradict the total sitting right
            above it, so all three are here and the panel ends on the same
            figure. */}
        {showParts && (
          <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
            {/* Renamed 21 October 2026: this and the charge list below
                were both called "What this bill is made of", one above
                the other on the same screen. This one answers where the
                debt SITS — room, departments, credit accounts, other
                rooms billed here. The one below is every charge. */}
            <span className="font-semibold">What is still owed, and where</span>
            <div className="mt-2">
              {billParts.filter(p => !p.onCredit).map(part => (
                <div key={part.key} className="flex items-baseline justify-between py-1.5">
                  <span className="text-dim">
                    {part.name}
                    {part.paid > 0 && (
                      <span className="block text-xs">{naira(part.paid)} paid against it</span>
                    )}
                  </span>
                  <span className={`tnum font-semibold ${part.left > 0.009 ? 'text-ink'
                    : part.left < -0.009 ? (part.key === 'room' ? 'text-leaf' : 'text-amber') : 'text-leaf'}`}>
                    {/* On the room, money beyond what is owed is nights paid
                        ahead, which is normal for a long stay and should not
                        be dressed up as a fault. On a department it is an
                        overpayment and worth a second look. */}
                    {part.left < -0.009
                      ? (part.key === 'room' ? `${naira(-part.left)} paid ahead` : `${naira(-part.left)} over`)
                      : part.left < 0.009 ? 'Settled' : naira(part.left)}
                  </span>
                </div>
              ))}
            </div>
            {generalPaid > 0.009 && (
              <p className="text-dim text-sm pt-3 mt-1 border-t border-line">
                {/* "Tick charges when taking money" outlived the ticking
                    itself by a day (21 October 2026). The front desk
                    stopped deciding what a payment settles on the 20th;
                    this was still telling them to do it. */}
                {naira(generalPaid)} was paid against the bill as a whole rather than
                any one charge, so it has gone to the room first and then the
                oldest charges. Each department records its own share once it
                has been handed the money.
              </p>
            )}

            {/* A different ledger. These were charged to a customer account
                linked to this guest, not to the room, and they clear through
                Credit — which is why they cannot be picked in Against below. */}
            {departmentCreditTotal > 0 && (
              <div className="pt-3 mt-3 border-t border-line">
                <span className="text-dim text-sm">On Credit — a separate account, payable here</span>
                {billParts.filter(p => p.onCredit).map(part => (
                  <div key={part.key} className="flex items-baseline justify-between py-1.5">
                    <span className="text-clay">
                      {part.name.replace(' — on Credit', '')}
                      {!part.applied && (
                        <span className="block text-xs text-dim">
                          More than one account — settle this on Credit
                        </span>
                      )}
                    </span>
                    <span className="tnum font-semibold text-clay">{naira(part.left)}</span>
                  </div>
                ))}
              </div>
            )}

            {billedToYouTotal > 0 && (
              <div className="pt-3 mt-3 border-t border-line">
                <span className="text-dim text-sm">Other rooms billed to this guest</span>
                {(billedToYou || []).filter(b => Number(b.outstanding) > 0.009).map(b => (
                  <div key={b.stay_id} className="flex items-baseline justify-between py-1.5">
                    <span className="text-clay">
                      {b.guest_name}{b.room_number ? ` · Room ${b.room_number}` : ''}
                    </span>
                    <span className="tnum font-semibold text-clay">{naira(Number(b.outstanding))}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Mirrors the box above word for word. It said "Everything
                outstanding ₦49,500" on a guest who was ₦49,500 AHEAD, which
                reads as a debt he does not have. */}
            <div className="flex items-baseline justify-between pt-3 mt-3 border-t border-line">
              <span className="font-semibold">
                {totalOutstanding < -0.009 ? 'Everything paid ahead' : 'Everything outstanding'}
              </span>
              <span className={`tnum font-bold ${totalOutstanding > 0 ? 'text-clay' : 'text-leaf'}`}>
                {naira(Math.abs(totalOutstanding))}
              </span>
            </div>
          </div>
        )}

        {showCountdown && (
          <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="font-semibold">Room rate — period progress</span>
              <span className="text-dim text-sm">{scheduledOut}</span>
            </div>
            <div className="h-2.5 rounded-full bg-line overflow-hidden">
              <div className="h-full bg-clay" style={{ width: `${pctElapsed}%` }} />
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-dim">
                {nightsElapsed} of {totalNights} night{totalNights > 1 ? 's' : ''} used
              </span>
              <span className="text-dim">{nightsLeft} left</span>
            </div>
            <div className="flex justify-between mt-3 pt-3 border-t border-line">
              <div>
                <div className="text-dim text-sm">Taken out so far</div>
                <div className="tnum font-bold">{naira(consumedRoomCharge)}</div>
              </div>
              <div className="text-right">
                <div className="text-dim text-sm">Left in the room rate</div>
                <div className="tnum font-bold text-leaf">{naira(remainingRoomCharge)}</div>
              </div>
            </div>
          </div>
        )}


        {live && (
          <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
            <div className="flex items-center justify-between">
              <div className="text-dim">Over-stay charge</div>
              {overstayDraft === null && (
                <button onClick={() => setOverstayDraft(currentOverstay != null ? String(currentOverstay) : '')}
                  className="text-dim text-sm underline">
                  {currentOverstay != null ? 'Change' : 'Set'}
                </button>
              )}
            </div>
            {overstayDraft === null ? (
              <p className="tnum mt-1">
                {currentOverstay != null ? naira(currentOverstay)
                  : <span className="text-dim">Not set — branch default is {naira(overstayDefault || 0)}</span>}
              </p>
            ) : (
              <div className="mt-2">
                <input type="number" inputMode="decimal" value={overstayDraft}
                  onChange={e => setOverstayDraft(e.target.value)}
                  placeholder={`Branch default: ${naira(overstayDefault || 0)}`}
                  className="h-12 w-full px-3 rounded-xl bg-raise border border-line tnum placeholder:text-dim" />
                {!canSetNonDefaultOverstay && (
                  <p className="text-dim text-sm mt-1">
                    Only GM or admin can set an amount other than the branch default, or remove it.
                    You can still set it to exactly {naira(overstayDefault || 0)}.
                  </p>
                )}
                <div className="flex gap-3 mt-2">
                  <button onClick={saveOverstay} disabled={busy}
                    className="flex-1 h-11 rounded-xl bg-amber text-bg font-semibold disabled:opacity-40">
                    Save
                  </button>
                  <button onClick={() => setOverstayDraft(null)} className="flex-1 h-11 text-dim">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* A guest who has left can still settle — paying at the desk on
            the way out, or later. Reopening the stay to take money was
            the only route before, which adds a reopen to the record and
            can change the nights charged. Shown after checkout only
            while something is actually owing, so a settled stay stays
            closed. */}
        {(live || (room.status === 'checked_out' && outstanding > 0.009)) && (
          <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
            <div className="text-dim mb-2">
              {live ? 'Record payment' : 'Record a payment after check-out'}
            </div>
            {!live && (
              <p className="text-dim text-sm mb-2">
                Checked out on {room.actual_out}. Recording this settles the balance
                without reopening the stay.
              </p>
            )}

            <label className="block text-dim text-sm">Amount</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="number" inputMode="decimal" value={pay.amount}
                onChange={e => setPay(p => ({ ...p, amount: e.target.value }))}
                className="h-12 flex-1 min-w-0 px-3 rounded-xl bg-raise border border-line tnum" />
              {outstanding > 0 && (
                <button onClick={() => setPay(p => ({ ...p, amount: String(outstanding) }))}
                  className="text-dim text-sm underline whitespace-nowrap">Full balance</button>
              )}
            </div>

            <label className="block text-dim text-sm mt-3">Paid by</label>
            <div className="mt-1">
              <PaymentMethodPicker methods={payMethods} amount={pay.amount}
                value={pay} onChange={v => setPay(p => ({ ...p, ...v }))} />
            </div>

            {/* WHAT THE BILL IS MADE OF — read only (Chris, 20 October
                2026). The desk reads this to the guest and takes the
                money; it does not decide whose money it is. Each
                department records what it was handed, on its own page,
                and that entry is what settles its charges.

                The lines are still here, and in more detail than
                before, because telling the guest what they owe is the
                front desk's job. Deciding who gets it is not. */}
            {payables.length > 0 && (
              <div className="mt-4">
                {/* THE WHOLE BILL, in one place (Chris, 20 October 2026).
                    It listed only what was still owed, so once the room
                    was paid and the departments had claimed their share
                    it emptied down to the credit accounts and looked as
                    though the charges had vanished.

                    It is read to the guest at checkout and printed as
                    their invoice, so it has to hold everything: every
                    charge, what has been paid, and what is left. */}
                <div className="flex items-baseline justify-between gap-3">
                  <label className="block font-semibold">What this bill is made of</label>
                  <button type="button" onClick={() => setPrinting(true)}
                    className="text-amber text-sm font-semibold shrink-0">
                    Print invoice
                  </button>
                </div>
                <p className="text-dim text-sm mt-0.5">
                  Read it to the guest. The money goes against the bill as a whole;
                  each department records its own share once it has been handed the
                  slip and the cash.
                </p>

                {/* Every charge, paid or not, with where its money has
                    got to. Built from the same billableLines and folio
                    figures the printed invoice uses, so the two cannot
                    disagree in front of a guest. */}
                <div className="mt-2 rounded-2xl border border-line bg-surface px-4 py-1">
                  <div className="py-2.5 flex items-baseline gap-3 border-b border-line/60">
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold leading-tight">Room charge</span>
                      <span className="block text-dim text-xs mt-0.5">
                        {folio?.nights} night{folio?.nights === 1 ? '' : 's'}
                        {folio?.daily_rate ? ` at ${naira(folio.daily_rate)}` : ''}
                      </span>
                    </span>
                    <span className="tnum font-bold shrink-0">{naira(folio?.room_charge ?? 0)}</span>
                  </div>

                  {billableLines.map(li => {
                    const got = Number((paidByItem || {})[li.id] || 0)
                    const r = (receiptByItem || {})[li.id]
                    const amt = Number(li.amount || 0)
                    const deptId = roomChargeDept(li, allLocations || locations)
                    const dept = (allLocations || locations || []).find(l => l.id === deptId)
                    return (
                      <div key={li.id} className="py-2.5 flex items-baseline gap-3 border-b border-line/60 last:border-0">
                        <span className="flex-1 min-w-0">
                          <span className="block font-semibold leading-tight break-words">
                            {li.qty} × {li.description}
                          </span>
                          <span className="block text-dim text-xs mt-0.5">
                            {dept ? `${dept.name} · ` : ''}{li.date}
                          </span>
                          {got >= amt - 0.009 && r && (
                            <span className="block text-leaf text-xs mt-0.5">
                              {r.dept} has this money
                            </span>
                          )}
                          {got < amt - 0.009 && Number(folio?.outstanding ?? 0) <= 0.009 && (
                            <span className="block text-amber text-xs mt-0.5">
                              Paid · the front desk still holds this
                            </span>
                          )}
                        </span>
                        <span className="tnum font-bold shrink-0">{naira(amt)}</span>
                      </div>
                    )
                  })}

                  {Number(folio?.overstay_charge ?? 0) > 0.009 && (
                    <div className="py-2.5 flex items-baseline gap-3 border-t border-line/60">
                      <span className="flex-1 min-w-0 font-semibold">Over-stay</span>
                      <span className="tnum font-bold shrink-0">{naira(folio.overstay_charge)}</span>
                    </div>
                  )}

                  <div className="py-2.5 border-t border-line space-y-1 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-dim">Total for the stay</span>
                      <span className="tnum font-bold">{naira(folio?.total_due ?? 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-dim">Paid so far</span>
                      <span className="tnum font-bold text-leaf">{naira(folio?.total_paid ?? 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3 pt-1 border-t border-line/60">
                      <span className="font-bold">Still to pay</span>
                      <span className={`tnum font-bold ${
                        Number(folio?.outstanding ?? 0) > 0.009 ? 'text-clay' : 'text-leaf'}`}>
                        {naira(folio?.outstanding ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* The credit accounts stay listed below, named for what
                    they are: another ledger, settled on the Credit page,
                    never by this payment. */}
                {/* Only the credit accounts now. The rest of what was
                    listed here is in the bill above, where it belongs —
                    repeating it underneath showed a guest the same
                    charge twice at the moment they were being asked for
                    money. */}
                {(() => {
                  const creditLines = payables.filter(p2 => p2.kind === 'credit')
                  if (!creditLines.length) return null
                  const creditTotal = creditLines.reduce((t, c) => t + c.amount, 0)
                  return (
                    <>
                      <div className="flex items-baseline justify-between gap-3 mt-4 mb-1">
                        <p className="text-dim text-sm">Owed on a department account</p>
                        <p className="tnum text-dim text-sm">{naira(creditTotal)}</p>
                      </div>
                      <div className="rounded-2xl border border-line bg-surface px-4">
                        {creditLines.map(l => (
                          <div key={l.key} className="py-2.5 flex items-baseline gap-3 border-b border-line/60 last:border-0">
                            <span className="flex-1 min-w-0">
                              <span className="block font-semibold leading-tight break-words">{l.what}</span>
                              <span className="block text-dim text-xs mt-0.5">{l.where}</span>
                            </span>
                            <span className="tnum font-bold shrink-0">{naira(l.amount)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-dim text-xs mt-1">
                        A separate ledger. This payment does not touch it — it is
                        settled on the Credit page.
                      </p>
                    </>
                  )
                })()}
              </div>
            )}

            {/* "Paid on a department's POS" used to sit here. Removed: a
                room bill is settled at the front desk, and OpenBar — the
                only department with a terminal of its own — does not serve
                rooms, so there is no department whose drawer the money
                could be in. It was being used to answer "whose charge was
                this?" instead, which tagged front-desk money to MainBar and
                left MainBar's Total Sales expecting POS that was never in
                it. Ticking the charges answers that question properly now.

                The "Change department" control that replaced it is gone
                too (20 October 2026) — see the Payments list below. */}

            {/* LAST, deliberately (18 October 2026). The date used to be
                the first field, so it was read before anyone had thought
                about the money and left on today by habit — which is how
                ₦30,000 of Mr Anthony's bill was recorded against 18
                September. The last thing touched before the button is
                the thing most likely to be checked. */}
            <label className="block text-dim text-sm">Date paid</label>
            {/* The floor used to be the stay's check-in date, which makes
                an advance payment impossible to date correctly: Mr Kalu
                booked ahead, paid on the 16th and arrived on the 17th,
                so the day he actually paid was greyed out. A deposit is
                paid BEFORE arrival by definition.
                Now the earlier of check-in and seven days back — the
                same window the sales screens allow — so a late entry
                can always be dated when the money changed hands, and a
                long stay keeps its whole length. */}
            <input type="date" value={pay.date || lagosToday()}
              min={[checkIn, lagosDaysAgo(7)].filter(Boolean).sort()[0] || undefined}
              max={lagosToday()}
              onChange={e => setPay(p => ({ ...p, date: e.target.value }))}
              className="mt-1 mb-3 h-12 w-full px-3 rounded-xl bg-raise border border-line tnum" />
            {pay.date && pay.date !== lagosToday() && (
              <p className="text-amber text-sm -mt-2 mb-3">
                Dating this payment {pay.date}. It will count towards that day's
                takings, not today's.
              </p>
            )}

            <label className="flex items-center gap-2 text-dim mt-3">
              <input type="checkbox" checked={isOverstay} onChange={e => setIsOverstay(e.target.checked)} />
              Over-stay payment
            </label>
            <button onClick={submitPayment}
              disabled={busy || payAllocated <= 0 || !payMethodChosen}
              className="mt-3 w-full h-14 rounded-2xl bg-amber text-bg text-lg font-bold disabled:opacity-40">
              {busy ? 'Recording…'
                : !payMethodChosen ? 'Choose how they paid'
                : `Record ${naira(payAllocated)}`}
            </button>

            {/* Put the bill on credit (567). Shown only while something is
                actually owed, and only to manager/gm/admin. The debt MOVES —
                the folio clears and a named account picks it up — so the
                same money never sits in two places. */}
            {outstanding > 0.009 && is(staff.role, MANAGEMENT) && (
              <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
                {!creditOpen ? (
                  <>
                    <div className="text-dim mb-1">Guest leaving without paying?</div>
                    <p className="text-dim text-sm">
                      Put the bill on a named account. The debt comes off this bill and
                      goes where Credit and Recovery can chase it.
                    </p>
                    <button onClick={openCredit}
                      className="mt-3 h-11 w-full rounded-xl border border-amber text-amber font-semibold">
                      Put {naira(outstanding)} on credit
                    </button>
                    {/* The panel above totals the room bill AND the department
                        credit accounts, so "Everything outstanding ₦328,900"
                        sits a few inches from a button offering ₦228,200 and
                        reads as a shortfall (Chris, 21 October 2026). It is
                        not: the rest is already a debt on an account. */}
                    {(departmentCreditTotal + billedToYouTotal) > 0.009 && (
                      <p className="text-dim text-xs mt-2">
                        This is the room bill. The other{' '}
                        {naira(departmentCreditTotal + billedToYouTotal)} shown above is
                        already owed on an account and is settled on Credit.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-semibold mb-2">Put this bill on credit</div>

                    <label className="block text-dim text-sm">How much</label>
                    <div className="flex items-center gap-3 mt-1">
                      <input type="number" inputMode="decimal" value={creditDraft?.amount || ''}
                        onChange={e => setCreditDraft(d => ({ ...d, amount: e.target.value }))}
                        className="h-12 flex-1 min-w-0 px-3 rounded-xl bg-raise border border-line tnum" />
                      <button onClick={() => setCreditDraft(d => ({ ...d, amount: String(Number(outstanding.toFixed(2))) }))}
                        className="text-dim text-sm underline whitespace-nowrap">Whole bill</button>
                    </div>

                    <label className="block text-dim text-sm mt-3">Whose account</label>
                    {creditDraft?.customerId ? (
                      <div className="mt-1 flex items-center gap-3">
                        <span className="flex-1 min-w-0 font-semibold break-words">
                          {(accounts || []).find(a => a.id === creditDraft.customerId)?.name}
                        </span>
                        <button onClick={() => setCreditDraft(d => ({ ...d, customerId: '' }))}
                          className="text-dim text-sm underline shrink-0">Change</button>
                      </div>
                    ) : (
                      <>
                        <input value={accountSearch} onChange={e => setAccountSearch(e.target.value)}
                          placeholder="Search existing accounts"
                          className="mt-1 h-11 w-full px-3 rounded-xl bg-raise border border-line placeholder:text-dim" />
                        {accountSearch.trim().length >= 2 && (
                          <ul className="mt-1 max-h-40 overflow-y-auto divide-y divide-line/60">
                            {(accounts || [])
                              .filter(a => a.name.toLowerCase().includes(accountSearch.trim().toLowerCase()))
                              .slice(0, 8)
                              .map(a => (
                                <li key={a.id}>
                                  <button onClick={() => { setCreditDraft(d => ({ ...d, customerId: a.id })); setAccountSearch('') }}
                                    className="w-full py-2 text-left break-words">{a.name}</button>
                                </li>
                              ))}
                            {accounts !== null
                              && !(accounts || []).some(a => a.name.toLowerCase().includes(accountSearch.trim().toLowerCase())) && (
                              <li className="py-2 text-dim text-sm">No account matches that.</li>
                            )}
                          </ul>
                        )}
                        <p className="text-dim text-xs mt-2">Or open a new account in this name:</p>
                        <input value={creditDraft?.name || ''}
                          onChange={e => setCreditDraft(d => ({ ...d, name: e.target.value }))}
                          className="mt-1 h-11 w-full px-3 rounded-xl bg-raise border border-line" />
                      </>
                    )}

                    <label className="block text-dim text-sm mt-3">Why (optional)</label>
                    <input value={creditDraft?.note || ''}
                      onChange={e => setCreditDraft(d => ({ ...d, note: e.target.value }))}
                      placeholder="Company settles monthly"
                      className="mt-1 h-11 w-full px-3 rounded-xl bg-raise border border-line placeholder:text-dim" />

                    <div className="flex gap-2 mt-4">
                      <button onClick={saveCredit} disabled={busy}
                        className="flex-1 h-12 rounded-xl bg-amber text-bg font-bold disabled:opacity-40">
                        Save
                      </button>
                      <button onClick={() => { setCreditOpen(false); setCreditDraft(null) }}
                        className="flex-1 h-12 rounded-xl border border-line text-dim">Cancel</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Awaiting front desk: shown so nobody is surprised at checkout,
            but NOT in the balance above until approved. */}
        {!!pendingLines.length && (
          <div className="mt-4 rounded-2xl border border-amber bg-surface p-4">
            <div className="flex justify-between">
              <span className="font-semibold">Awaiting front-desk approval</span>
              <span className="tnum font-bold">
                {naira(pendingLines.reduce((t, l) => t + Number(l.amount || 0), 0))}
              </span>
            </div>
            <p className="text-dim text-xs mt-1">
              Not on this guest's bill until approved. Approve or refuse it from the
              Reception dashboard.
            </p>
          </div>
        )}

        {orderLines.length > 0 && (
          <div className="mt-4">
            {/* Payment is recorded against the WHOLE stay, not line by
                line, so no single charge can be marked paid. State the
                folio's position instead of implying per-item tracking. */}
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-dim">Orders</span>
              {outstanding > 0.009
                ? <span className="text-clay text-sm font-semibold">
                    {naira(outstanding)} of this bill is unpaid
                  </span>
                : <span className="text-leaf text-sm font-semibold">
                    Bill settled in full
                  </span>}
            </div>
            <ul className="divide-y divide-line/60 rounded-2xl border border-line bg-surface px-4">
              {orderLines.map(li => (
                <li key={li.id} className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="truncate flex items-center gap-2">
                        <span className="truncate">{li.description}</span>
                        {li.order_type === 'pr_damage' && (
                          <span className="shrink-0 text-xs font-bold text-clay border border-clay rounded-full px-2 py-0.5">
                            PR / Damage
                          </span>
                        )}
                        {li.order_type === 'staff' && (
                          <span className="shrink-0 text-xs font-bold text-amber border border-amber rounded-full px-2 py-0.5">
                            Staff
                          </span>
                        )}
                        {li.settlement !== 'charged_to_room' && (
                          <span className="shrink-0 text-xs font-bold text-leaf border border-leaf rounded-full px-2 py-0.5">
                            Paid at dept
                          </span>
                        )}
                        {li.approval === 'pending' && (
                          <span className="shrink-0 text-xs font-bold text-amber border border-amber rounded-full px-2 py-0.5">
                            Awaiting approval
                          </span>
                        )}
                        {li.approval === 'rejected' && (
                          <span className="shrink-0 text-xs font-bold text-clay border border-clay rounded-full px-2 py-0.5">
                            Refused
                          </span>
                        )}
                        {/* Dated wrongly at the till? A GM can move it to
                            the right day without touching the amount. */}
                        {is(staff.role, SUPERVISOR) && (
                          <button
                            onClick={() => setMovingCharge({ id: li.id, date: li.date,
                                                             description: li.description })}
                            className="shrink-0 text-xs text-dim underline">
                            {li.date}
                          </button>
                        )}
                      </div>
                      {/* Where this charge's money has got to (564).
                          Three states, and the middle one is the one
                          that used to be invisible: the guest has paid
                          and the department has not yet been handed it.
                          A charge does not disappear once it is
                          recorded — it says who has the money. */}
                      {(() => {
                        const got = Number((paidByItem || {})[li.id] || 0)
                        const r = (receiptByItem || {})[li.id]
                        const amt = Number(li.amount || 0)
                        if (got >= amt - 0.009 && r) {
                          return (
                            <div className="text-leaf text-xs font-semibold mt-0.5">
                              {r.dept} has this money{r.by ? ` · recorded by ${r.by}` : ''}
                            </div>
                          )
                        }
                        if (got >= amt - 0.009) {
                          return <div className="text-leaf text-xs font-semibold mt-0.5">Settled</div>
                        }
                        if (Number(folio?.outstanding ?? 0) <= 0.009
                            && li.settlement === 'charged_to_room') {
                          return (
                            <div className="text-amber text-xs font-semibold mt-0.5">
                              Guest has paid · the front desk still holds this
                              {got > 0.009 && <> · {naira(amt - got)} of it</>}
                            </div>
                          )
                        }
                        return null
                      })()}
                      <div className="text-dim text-sm">
                        {/* Whose money this is (20 October 2026). The line
                            said what was sold and what it cost, and never
                            which department sold it — so a clerk taking
                            payment at checkout could not see from the bill
                            who the money belongs to. Resolved the same way
                            every other page resolves it, so a bar's charge
                            cannot be read as the other bar's. */}
                        {(() => {
                          const deptId = roomChargeDept(li, allLocations || locations)
                          const dept = (allLocations || locations || []).find(l => l.id === deptId)
                          return dept ? <span className="text-ink font-semibold">{dept.name} · </span> : null
                        })()}
                        {li.date} · {li.qty} × {naira(li.unit_price)}
                        {li.order_type === 'pr_damage' && ' · not charged to guest'}
                        {(li.damage_reason || li.pr_meal) && ` · ${li.damage_reason || li.pr_meal}`}
                        {li.writeoff_note && ` · ${li.writeoff_note}`}
                      </div>
                    </div>
                    <span className="tnum font-semibold">{naira(li.amount)}</span>
                  </div>
                  {(() => {
                    const canDelete = is(staff.role, SUPERVISOR)
                    const canEdit = canDelete || li.servedBy === staff.id
                    if (!canEdit) return null
                    return (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => openEditLine(li)}
                          className="h-9 px-3 rounded-lg border border-line text-sm font-semibold">
                          Edit
                        </button>
                        {canDelete && (
                          <button onClick={() => setDeletingLine(li)}
                            className="h-9 px-3 rounded-lg border border-clay text-clay text-sm font-semibold">
                            Delete
                          </button>
                        )}
                      </div>
                    )
                  })()}
                </li>
              ))}
            </ul>
          </div>
        )}

        {payments.length > 0 && (
          <div className="mt-4">
            <div className="text-dim mb-2">Payments</div>
            <ul className="divide-y divide-line/60 rounded-2xl border border-line bg-surface px-4">
              {payments.map(p => (
                <li key={p.id} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      {p.method.toUpperCase()}{p.is_overstay ? ' · over-stay' : ''}
                    </div>
                    <div className="text-dim text-sm">{p.business_date}{p.remark ? ` · ${p.remark}` : ''}</div>
                    {p.collected?.name && (
                      <div className="text-amber text-sm">Collected at {p.collected.name}</div>
                    )}
                    {/* What the guest asked this money to clear. Absent on
                        every payment taken before migration 496, and on any
                        the desk left against the bill as a whole. */}
                    {p.applied_to && (
                      <div className="text-leaf text-sm">
                        Clears {p.applied_to === 'room' ? 'the room charge'
                          : locName(p.applied_location_id) || 'a department'}
                      </div>
                    )}
                    {/* Both controls that used to sit here are gone
                        (Chris, 20 October 2026). "Say what this payment was
                        for" and "Change department" let the desk — or a GM
                        standing at it — re-point money after the fact, and
                        the front desk does not decide whose money a room
                        payment is any more: it accepts the room bill, and
                        each department records what it was physically
                        handed.

                        The sharper reason is that these buttons rendered on
                        EVERY payment row, including a department's own
                        receipt recorded through 564. Moving one of those
                        would have shifted the till figure away from the
                        charge it settled and left the two disagreeing with
                        nothing on screen to show it.

                        A payment keyed at the wrong till is a correction to
                        the record, and corrections to money are made by id,
                        with the before and after read first — not by a
                        dropdown on a live guest's bill. */}
                  </div>
                  <span className="tnum font-semibold">{naira(p.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What earlier moves put on this bill. Without this the total
            carries money with nothing on screen explaining it. */}
        {!!moves.length && (
          <div className="mt-6">
            <h3 className="font-semibold mb-2">Room changes</h3>
            <ul className="divide-y divide-line/60 rounded-2xl border border-line bg-surface px-4">
              {moves.map(mv => {
                const a = Number(mv.adjustment || 0)
                const undone = !!mv.undone_at
                // Only the LAST live move can be taken back: moves
                // compose, so removing one from the middle would leave
                // the guest in a room no sequence of moves arrives at.
                const isLast = !undone
                  && mv.id === [...moves].reverse().find(x => !x.undone_at)?.id
                // The move covered nights up to here. If the stay's
                // dates have changed since, the frozen amount no longer
                // matches the stay — see below.
                const impliedEnd = mv.moved_on && mv.nights_affected != null
                  ? addDays(mv.moved_on, mv.nights_affected) : null
                const nowEnd = room.actual_out || scheduledOut || room.scheduled_out
                const stale = !undone && impliedEnd && nowEnd && impliedEnd !== nowEnd
                return (
                  <li key={mv.id} className="py-3">
                    <div className="flex items-baseline gap-3">
                      <span className={`flex-1 min-w-0 ${undone ? 'opacity-50' : ''}`}>
                        <span className="font-semibold break-words block">
                          Room {mv.from_r?.room_number || '?'} → Room {mv.to_r?.room_number || '?'}
                          {undone && <span className="text-dim font-normal"> · taken back</span>}
                        </span>
                        <span className="text-dim text-sm block tnum">
                          {mv.moved_on}
                          {mv.nights_affected ? ` · ${mv.nights_affected} night${mv.nights_affected === 1 ? '' : 's'}` : ''}
                          {mv.staff?.full_name ? ` · ${mv.staff.full_name}` : ''}
                        </span>
                        {mv.reason && <span className="text-dim text-xs block break-words">{mv.reason}</span>}
                        {undone && mv.undo_reason && (
                          <span className="text-dim text-xs block break-words">
                            taken back by {mv.undoer?.full_name || 'someone'} — {mv.undo_reason}
                          </span>
                        )}
                      </span>
                      <span className={`tnum font-semibold shrink-0 ${undone ? 'text-dim line-through'
                        : a > 0.009 ? 'text-clay' : a < -0.009 ? 'text-leaf' : 'text-dim'}`}>
                        {a > 0.009 ? naira(a) : a < -0.009 ? `− ${naira(-a)}` : 'no charge'}
                      </span>
                    </div>
                    {/* The stay's dates moved after this charge was
                        frozen, so the nights it paid for are no longer
                        the nights they are staying. Said, not silently
                        recomputed — the whole point of freezing it is
                        that money does not move on its own. */}
                    {stale && (
                      <p className="text-clay text-sm mt-1">
                        This covered {mv.nights_affected} night{mv.nights_affected === 1 ? '' : 's'} to {impliedEnd},
                        but the stay now runs to {nowEnd}. Check the amount is still right —
                        take it back and make it again if it is not.
                      </p>
                    )}
                    {isLast && canManage && (
                      <button onClick={() => setUndoing({ move: mv, reason: '' })} disabled={busy}
                        className="mt-2 h-9 px-3 rounded-lg border border-clay text-clay text-sm font-semibold disabled:opacity-40">
                        Take this back
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {live && (
          <div className="mt-6">
            {/* Moving comes before checking out: it is the thing you do to
                a guest who is staying, and burying it under More would
                mean a plumbing fault at 11pm turns into a deletion. */}
            <button onClick={openMove} disabled={busy}
              className="w-full h-14 rounded-2xl border border-line text-ink font-semibold mb-3 disabled:opacity-40">
              Move to another room
            </button>
            {outstanding > 0 && (
              <p className="text-clay text-sm mb-2">{naira(outstanding)} is still outstanding on this room.</p>
            )}
            <button onClick={doCheckOut} disabled={busy}
              className={`w-full h-16 rounded-2xl text-xl font-bold disabled:opacity-40 ${
                outstanding > 0 ? 'bg-clay text-bg' : 'bg-amber text-bg'}`}>
              {outstanding > 0 ? 'Check out with balance owing' : 'Check out'}
            </button>
          </div>
        )}

        {!live && room.status === 'checked_out' && (() => {
          const closedToday = room.actual_out === lagosToday()
          const canReopen = closedToday || SUPERVISOR_ROLES.includes(staff.role)
          if (!canReopen) return (
            <p className="mt-6 text-dim text-sm">
              Checked out on {room.actual_out}. Only a manager can reopen a stay closed on an earlier day.
            </p>
          )
          return (
            <div className="mt-6">
              <p className="text-dim text-sm mb-2">
                {closedToday ? 'Checked out earlier today.' : `Checked out on ${room.actual_out}.`}
                {outstanding > 0 && ' The balance above is still owing.'}
              </p>
              <button onClick={doReopen} disabled={busy}
                className="w-full h-14 rounded-2xl border border-line text-ink font-semibold disabled:opacity-40">
                Undo check-out — guest is still in the room
              </button>
            </div>
          )
        })()}
      </div>

      {groupOpen && groupDraft && (
        <div className="fixed inset-0 z-[60] bg-bg overflow-y-auto p-5">
          <button onClick={() => { setGroupOpen(false); setGroupDraft(null) }}
            className="text-dim">Back</button>
          <h2 className="mt-3 text-2xl font-bold">Settle rooms billed here</h2>
          <p className="text-dim text-sm mt-1">
            {groupRooms.length} room{groupRooms.length === 1 ? '' : 's'} owing {naira(billedToYouTotal)}.
          </p>

          <label className="block mt-4 text-dim">How much was paid</label>
          <input type="number" inputMode="decimal" value={groupDraft.amount}
            onChange={e => setGroupDraft(d => ({ ...d, amount: e.target.value }))}
            className="w-full h-12 px-3 rounded-xl bg-raise border border-line tnum text-lg" />

          <label className="block mt-4 text-dim">How</label>
          <div className="flex gap-2 mt-1">
            {payMethods.map(m => (
              <button key={m} onClick={() => setGroupDraft(d => ({ ...d, method: m }))}
                className={`flex-1 h-12 rounded-xl border font-semibold uppercase ${
                  groupDraft.method === m ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
                {m}
              </button>
            ))}
          </div>

          <label className="block mt-4 text-dim">Date</label>
          <input type="date" value={groupDraft.date}
            onChange={e => setGroupDraft(d => ({ ...d, date: e.target.value }))}
            className="w-full h-12 px-3 rounded-xl bg-raise border border-line" />

          <label className="block mt-4 text-dim">Note (optional)</label>
          <input value={groupDraft.note}
            onChange={e => setGroupDraft(d => ({ ...d, note: e.target.value }))}
            placeholder="Who paid, and for what"
            className="w-full h-12 px-3 rounded-xl bg-raise border border-line" />

          {/* Shown before anything is written. A group payment touches
              rooms the person cannot see from here, so what it is about
              to do is put in front of them first. */}
          <div className="mt-5 rounded-xl border border-line overflow-hidden">
            <p className="px-3 py-2 bg-raise font-semibold text-sm">What this pays</p>
            {groupSpread.length ? groupSpread.map(b => (
              <div key={b.stay_id} className="px-3 py-2 flex justify-between gap-3 border-t border-line">
                <span className="text-sm truncate">
                  Room {b.room_number || '—'} · {b.guest_name}
                  {b.take < Math.round(Number(b.outstanding) * 100) / 100 && (
                    <span className="text-dim"> · part of {naira(b.outstanding)}</span>
                  )}
                </span>
                <span className="tnum text-sm font-semibold shrink-0">{naira(b.take)}</span>
              </div>
            )) : (
              <p className="px-3 py-2 text-dim text-sm border-t border-line">
                Enter an amount to see which rooms it pays.
              </p>
            )}
          </div>

          {Number(groupDraft.amount) > billedToYouTotal + 0.009 && (
            <p className="text-clay text-sm mt-3 font-semibold">
              That is more than the {naira(billedToYouTotal)} these rooms owe. Reduce it, and put
              anything extra on a room's own bill where it can be seen.
            </p>
          )}

          <button onClick={saveGroup}
            disabled={busy || !(Number(groupDraft.amount) > 0) || !groupDraft.method
              || Number(groupDraft.amount) > billedToYouTotal + 0.009}
            className="mt-6 w-full h-14 rounded-2xl bg-amber text-bg text-lg font-bold disabled:opacity-40">
            {busy ? 'Saving…'
              : !groupDraft.method ? 'Choose how they paid'
              : `Record ${naira(Number(groupDraft.amount) || 0)}`}
          </button>
        </div>
      )}

      {linkFix && (
        <div className="fixed inset-0 z-[60] bg-bg overflow-y-auto p-5">
          <button onClick={() => { setLinkFix(null); setLinkQuery(''); setLinkResults([]) }}
            className="text-dim">Back</button>
          <h2 className="mt-3 text-2xl font-bold">Who does this account belong to?</h2>
          <p className="text-dim mt-1">{linkFix.customerName}</p>
          <p className="text-dim text-sm mt-3">
            This moves no money. The balance stays on the account either way —
            all that changes is whose folio it shows on.
          </p>

          <label className="block mt-5 text-dim">Point it at another guest</label>
          <input value={linkQuery} onChange={e => setLinkQuery(e.target.value)}
            placeholder="Type a guest's name"
            className="mt-1 h-14 w-full px-4 rounded-xl bg-surface border border-line placeholder:text-dim" />
          {!!linkResults.length && (
            <div className="mt-2 rounded-xl border border-amber bg-surface divide-y divide-line overflow-hidden">
              {linkResults.map(g => (
                <button key={g.id} type="button" disabled={busy}
                  onClick={() => applyLinkFix(g)}
                  className="block w-full text-left px-4 py-3 disabled:opacity-40">
                  <div className="font-semibold">{g.full_name}</div>
                  {g.phone && <div className="text-dim text-sm">{g.phone}</div>}
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 pt-5 border-t border-line">
            <button onClick={() => applyLinkFix(null)} disabled={busy}
              className="w-full h-14 rounded-2xl border border-clay text-clay font-bold disabled:opacity-40">
              {busy ? 'Working…' : 'Remove the link'}
            </button>
            <p className="text-dim text-xs mt-2">
              The balance comes off every folio and is chased on Credit, like any
              other account.
            </p>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] bg-bg overflow-y-auto p-5">
          <button onClick={() => setEditing(null)} className="text-dim">Back</button>
          <h2 className="mt-3 text-2xl font-bold">Edit rate & dates</h2>

          <label className="block mt-4 text-dim">Daily rate</label>
          <input type="number" inputMode="decimal" value={editing.dailyRate}
            onChange={e => setEditing(x => ({ ...x, dailyRate: e.target.value }))}
            className="mt-1 h-14 w-full px-4 rounded-xl bg-surface border border-line tnum" />

          <label className="block mt-4 text-dim">Billing cycle</label>
          <div className="mt-1 flex gap-2">
            {cycles.map(c => (
              <button key={c.value} onClick={() => setEditing(x => ({ ...x, billingCycle: c.value }))}
                className={`flex-1 h-12 rounded-xl border font-semibold ${editing.billingCycle === c.value
                  ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
                {c.label}
              </button>
            ))}
          </div>

          <label className="block mt-4 text-dim">Scheduled check-out</label>
          <input type="date" value={editing.scheduledOut} min={room.check_in_date}
            onChange={e => setEditing(x => ({ ...x, scheduledOut: e.target.value }))}
            className="mt-1 h-14 px-4 rounded-xl bg-surface border border-line tnum" />

          <label className="block mt-4 text-dim">Reason for the change (optional, but worth noting)</label>
          <input value={editing.rateReason}
            onChange={e => setEditing(x => ({ ...x, rateReason: e.target.value }))}
            className="mt-1 h-14 w-full px-4 rounded-xl bg-surface border border-line" />

          <label className="block mt-4 text-dim">Billed to (optional)</label>
          <input value={editing.billTo}
            onChange={e => { setEditing(x => ({ ...x, billTo: e.target.value, billToGuestId: null })); setBillToOpen(true) }}
            onFocus={() => setBillToOpen(true)}
            placeholder="Leave blank if the guest pays their own bill"
            className="mt-1 h-14 w-full px-4 rounded-xl bg-surface border border-line placeholder:text-dim" />
          {billToOpen && !!billToSuggestions.length && (
            <div className="mt-2 rounded-xl border border-amber bg-surface divide-y divide-line overflow-hidden">
              <div className="px-4 py-2 text-dim text-sm">Is this an existing guest?</div>
              {billToSuggestions.map(g => (
                <button key={g.id} type="button"
                  onClick={() => {
                    setEditing(x => ({ ...x, billTo: g.full_name, billToGuestId: g.id }))
                    setBillToOpen(false); setBillToSuggestions([])
                  }}
                  className="block w-full text-left px-4 py-3 hover:bg-raise">
                  <div className="font-semibold">{g.full_name}</div>
                  {g.phone && <div className="text-dim text-sm">{g.phone}</div>}
                </button>
              ))}
              <button type="button" onClick={() => { setBillToOpen(false); setBillToSuggestions([]) }}
                className="block w-full text-center px-4 py-2 text-dim text-sm">
                No — not a guest
              </button>
            </div>
          )}
          {editing.billToGuestId && (
            <p className="text-dim text-xs mt-1">Linked — this amount will also show on their own folio.</p>
          )}

          <button onClick={saveEdit} disabled={busy || !editing.dailyRate || !editing.scheduledOut}
            className="mt-6 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}

      {printing && (
        <FolioStatement room={room} folio={folio} orderLines={billableLines} moves={moves}
          freeLines={freeLines} payments={payments} checkedInAt={showTimes ? stay?.checked_in_at : null}
          departmentCredit={departmentCredit} billedToYou={billedToYou}
          allLocations={allLocations || locations}
          branchName={boot.branchName} onClose={() => setPrinting(false)} />
      )}

      {/* MOVING A GUEST (576). The arithmetic is shown before anything is
          saved, the same principle as the crate receiving: a figure
          nobody reads is the one that runs wrong for months. */}
      {moving && (() => {
        const toRoom = (freeRooms || []).find(r => r.id === moving.toRoomId)
        const rateField = { standard: 'rate_standard', alternate: 'rate_alternate',
                            short: 'rate_short', special: 'rate_special' }[stay?.rate_applied] || 'rate_standard'
        const newRate = toRoom ? Number(toRoom[rateField] || 0) : null
        const oldRate = Number(folio?.daily_rate ?? room.daily_rate ?? 0)
        const end = scheduledOut || room.scheduled_out
        const nights = (moving.on && end)
          ? Math.max(0, Math.round((new Date(end) - new Date(moving.on)) / 864e5)) : 0
        const diff = moving.kind === 'fault' ? 0 : (newRate == null ? 0 : newRate - oldRate)
        const adjust = moving.override !== '' ? Number(moving.override) || 0 : nights * diff
        const ready = !!moving.toRoomId && !!moving.on
          && (moving.override === '' || !!moving.reason.trim())
        return (
        <div className="fixed inset-0 z-[60] bg-bg h-[100dvh] overflow-y-auto overscroll-contain p-5">
          <button onClick={() => setMoving(null)} className="text-dim">Back</button>
          <h3 className="mt-3 text-2xl font-bold">Move to another room</h3>
          <p className="text-dim text-sm mt-1 mb-4">
            {room.guest_name || 'Guest'} · in Room {room.room_number} since {checkIn}.
            The bill, the payments and anything charged from the bars move with them.
          </p>

          <div className="text-dim text-sm mb-1">Why</div>
          <div className="flex gap-2">
            {[['fault', 'A fault'], ['upgrade', 'Upgrade'], ['downgrade', 'Downgrade'], ['other', 'Other']].map(([k, label]) => (
              <button key={k} onClick={() => setMoving(m => ({ ...m, kind: k }))}
                className={`flex-1 h-12 rounded-xl border text-sm font-semibold ${moving.kind === k
                  ? 'bg-raise border-amber text-amber' : 'border-line text-dim'}`}>
                {label}
              </button>
            ))}
          </div>
          {moving.kind === 'fault' && (
            <p className="text-dim text-sm mt-2">
              Our fault, our cost — the guest pays exactly what they agreed, whichever
              room they end up in.
            </p>
          )}

          <div className="text-dim text-sm mt-4 mb-1">New room</div>
          {freeRooms === null && <p className="text-dim text-sm">Finding free rooms…</p>}
          {freeRooms?.length === 0 && (
            <p className="text-clay text-sm">
              No other room is free for the whole of this stay ({checkIn} to {end}).
              A room free only for the nights left cannot be used — the booking covers
              the whole stay.
            </p>
          )}
          {!!freeRooms?.length && (
            <select value={moving.toRoomId}
              onChange={e => setMoving(m => ({ ...m, toRoomId: e.target.value }))}
              className="h-12 w-full px-3 rounded-xl bg-raise border border-line">
              <option value="">Pick a room</option>
              {freeRooms.map(r => (
                <option key={r.id} value={r.id}>
                  Room {r.room_number}
                  {r.room_categories?.name ? ` · ${r.room_categories.name}` : ''}
                  {` · ${naira(Number(r[rateField] || 0))}`}
                </option>
              ))}
            </select>
          )}

          <div className="text-dim text-sm mt-4 mb-1">Day they moved</div>
          <input type="date" value={moving.on} min={checkIn} max={lagosToday()}
            onChange={e => setMoving(m => ({ ...m, on: e.target.value }))}
            className="h-12 px-3 rounded-xl bg-raise border border-line tnum" />

          {/* What it does to the bill, in full, before it is done. */}
          {toRoom && (
            <div className="mt-4 rounded-xl border border-line bg-raise p-3">
              <div className="text-dim text-sm tnum">
                Room {room.room_number} {naira(oldRate)} → Room {toRoom.room_number} {naira(newRate)}
                {' · '}{nights} night{nights === 1 ? '' : 's'} left
              </div>
              <div className={`mt-1 font-semibold tnum ${
                adjust > 0.009 ? 'text-clay' : adjust < -0.009 ? 'text-leaf' : 'text-dim'}`}>
                {adjust > 0.009 ? `${nights} × ${naira(diff)} = ${naira(adjust)} added to the bill`
                  : adjust < -0.009 ? `${nights} × ${naira(-diff)} = ${naira(-adjust)} refunded`
                  : 'No change to the bill'}
              </div>
              <div className="text-dim text-sm mt-1 tnum">
                Outstanding would become {naira(Number(outstanding || 0) + adjust)}.
              </div>
            </div>
          )}

          <div className="text-dim text-sm mt-4 mb-1">
            Different amount? (leave blank for the figure above)
          </div>
          <input type="number" inputMode="decimal" value={moving.override}
            onChange={e => setMoving(m => ({ ...m, override: e.target.value }))}
            placeholder="blank"
            className="h-12 w-full px-3 rounded-xl bg-raise border border-line tnum" />

          <div className="text-dim text-sm mt-4 mb-1">
            Note{moving.override !== '' ? ' — required, because the amount was changed' : ''}
          </div>
          <input type="text" value={moving.reason}
            onChange={e => setMoving(m => ({ ...m, reason: e.target.value }))}
            placeholder="Blocked drain in 204"
            className="h-12 w-full px-3 rounded-xl bg-raise border border-line" />

          <label className="mt-4 flex items-center gap-3">
            <input type="checkbox" checked={moving.outOfService} className="w-5 h-5"
              onChange={e => setMoving(m => ({ ...m, outOfService: e.target.checked }))} />
            <span className="text-sm">
              Take Room {room.room_number} out of service — it stops being offered until
              someone puts it back
            </span>
          </label>

          <button onClick={saveMove} disabled={busy || !ready}
            className="mt-5 h-12 w-full rounded-xl bg-amber text-bg font-bold disabled:opacity-40">
            {busy ? 'Moving…'
              : toRoom ? `Move to Room ${toRoom.room_number}` : 'Move'}
          </button>
          <div className="h-8" />
        </div>
        )
      })()}

      {undoing && (
        <div className="fixed inset-0 z-[60] bg-bg flex flex-col justify-center px-6">
          <h2 className="text-2xl font-bold">Take this room change back?</h2>
          <p className="text-dim mt-3">
            Room {undoing.move.from_r?.room_number || '?'} → Room {undoing.move.to_r?.room_number || '?'}
            {' '}on {undoing.move.moved_on}.
          </p>
          <p className="text-dim mt-3 tnum">
            {Number(undoing.move.adjustment) > 0.009
              ? `${naira(undoing.move.adjustment)} comes off the bill, and the guest goes back to Room ${undoing.move.from_r?.room_number || '?'}.`
              : Number(undoing.move.adjustment) < -0.009
                ? `${naira(-Number(undoing.move.adjustment))} goes back onto the bill, and the guest goes back to Room ${undoing.move.from_r?.room_number || '?'}.`
                : `The bill does not change. The guest goes back to Room ${undoing.move.from_r?.room_number || '?'}.`}
          </p>
          <p className="text-dim text-sm mt-3">
            The change stays on the record, marked as taken back, with your name on it.
            {' '}If the room was taken out of service it stays out — put it back from the
            Rooms board when it is actually fixed.
          </p>

          <label className="block mt-5 text-dim text-sm" htmlFor="undo-reason">Why</label>
          <input id="undo-reason" type="text" value={undoing.reason}
            onChange={e => setUndoing(u => ({ ...u, reason: e.target.value }))}
            placeholder="Wrong room picked"
            className="mt-2 h-12 w-full px-3 rounded-xl bg-surface border border-line" />

          <button onClick={doUndoMove} disabled={busy}
            className="mt-6 w-full h-14 rounded-2xl border border-clay text-clay font-bold disabled:opacity-40">
            {busy ? 'Taking it back…' : 'Take it back'}
          </button>
          <button onClick={() => setUndoing(null)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </div>
      )}

      {checkoutDate && (
        <div className="fixed inset-0 z-[60] bg-bg flex flex-col justify-center px-6">
          <h2 className="text-2xl font-bold">Which day did they leave?</h2>
          <p className="text-dim mt-2">
            {room.guest_name || 'Guest'} · Room {room.room_number}
          </p>

          {stay?.pre_reopen_actual_out ? (
            <p className="text-amber text-sm mt-3">
              This stay was reopened. Before that it was checked out on{' '}
              <b>{stay.pre_reopen_actual_out}</b>. Keep that date unless the
              guest really stayed on — a later date charges the nights in
              between.
            </p>
          ) : (
            <p className="text-dim text-sm mt-3">
              Today unless they left earlier. If nobody checked them out on the
              day, put the day they actually left — the nights in between are
              charged otherwise.
            </p>
          )}

          <label className="block mt-6 text-dim" htmlFor="departure-date">Departure date</label>
          <input id="departure-date" type="date" value={checkoutDate} max={lagosToday()}
            min={checkIn || undefined}
            onChange={e => setCheckoutDate(e.target.value)}
            className="mt-2 h-14 w-full px-4 rounded-xl bg-surface border border-line tnum" />

          {/* Nights, not naira. What the stay finally comes to is the
              folio's arithmetic, and a second figure worked out here could
              disagree with it. Nights cannot. */}
          {checkIn && checkoutDate && (
            <p className="text-dim text-sm mt-3 tnum">
              Leaving on this day bills{' '}
              <b className="text-ink">{nightsBetween(checkIn, checkoutDate)} night{nightsBetween(checkIn, checkoutDate) === 1 ? '' : 's'}</b>
              {checkoutDate !== lagosToday() && (
                <> — today&rsquo;s date would bill {nightsBetween(checkIn, lagosToday())}.</>
              )}
            </p>
          )}

          {stay?.pre_reopen_actual_out && checkoutDate !== stay.pre_reopen_actual_out && (
            <p className="text-clay text-sm mt-3">
              Not the date it had before — this will change what the room
              charge comes to.
            </p>
          )}

          <button onClick={() => finishCheckOut(checkoutDate)} disabled={busy || !checkoutDate}
            className="mt-8 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Checking out…' : `Check out on ${checkoutDate}`}
          </button>
          <button onClick={() => setCheckoutDate(null)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </div>
      )}

      {confirmingDelete && (
        <div className="fixed inset-0 z-[60] bg-bg flex flex-col justify-center px-6">
          <h2 className="text-2xl font-bold">Delete this booking?</h2>
          <p className="text-dim mt-2">
            {room.guest_name || 'Guest'} · Room {room.room_number}
          </p>
          <p className="text-dim text-sm mt-2">
            Removes the stay along with every room charge and payment attached
            to it. For training records only — this cannot be undone.
          </p>
          <button onClick={doDeleteStay} disabled={busy}
            className="mt-8 w-full h-16 rounded-2xl bg-clay text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Deleting…' : 'Delete booking'}
          </button>
          <button onClick={() => setConfirmingDelete(false)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </div>
      )}

      {editingLine && (
        <div className="fixed inset-0 z-[60] bg-bg flex flex-col justify-center px-6">
          <h2 className="text-2xl font-bold">Edit this order</h2>

          <label className="block mt-6 text-dim">Description</label>
          <input value={lineDraft.description}
            onChange={e => setLineDraft(d => ({ ...d, description: e.target.value }))}
            className="mt-2 h-14 w-full px-4 rounded-xl bg-surface border border-line" />

          <label className="block mt-4 text-dim">Quantity</label>
          <input type="number" inputMode="decimal" value={lineDraft.qty}
            onChange={e => setLineDraft(d => ({ ...d, qty: e.target.value }))}
            className="mt-2 h-14 w-full px-4 rounded-xl bg-surface border border-line tnum" />

          <label className="block mt-4 text-dim">Unit price</label>
          <input type="number" inputMode="decimal" value={lineDraft.unitPrice}
            onChange={e => setLineDraft(d => ({ ...d, unitPrice: e.target.value }))}
            className="mt-2 h-14 w-full px-4 rounded-xl bg-surface border border-line tnum" />

          <button onClick={saveEditLine} disabled={busy || !lineDraft.description.trim() || !lineDraft.qty}
            className="mt-8 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button onClick={() => setEditingLine(null)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </div>
      )}

      {deletingLine && (
        <div className="fixed inset-0 z-[60] bg-bg flex flex-col justify-center px-6">
          <h2 className="text-2xl font-bold">Delete this order?</h2>
          <p className="text-dim mt-2">
            {deletingLine.description} — {deletingLine.qty} × {naira(deletingLine.unit_price)}
          </p>
          <p className="text-dim text-sm mt-2">
            Removes this charge from the guest's bill and reverses any stock it
            deducted. This cannot be undone.
          </p>
          <button onClick={doDeleteLine} disabled={busy}
            className="mt-8 w-full h-16 rounded-2xl bg-clay text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Deleting…' : 'Delete order'}
          </button>
          <button onClick={() => setDeletingLine(null)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </div>
      )}

    {/* Folio has no Sheet helper of its own — this matches the
        full-screen overlay its other panels use. */}
    {movingCharge && (
      <div className="fixed inset-0 z-50 bg-bg overflow-y-auto">
        <div className="px-5 py-6">
          <button onClick={() => setMovingCharge(null)} className="text-dim">Back</button>
          <h3 className="mt-3 text-2xl font-bold">Move this charge</h3>
          <p className="text-dim text-sm mt-1">{movingCharge.description}</p>

          <label className="block text-dim text-sm mt-5">Date of the charge</label>
          <input type="date" value={movingCharge.date || ''}
            min={checkIn || undefined} max={lagosToday()}
            onChange={e => setMovingCharge(m => ({ ...m, date: e.target.value }))}
            className="mt-1 h-12 w-full px-3 rounded-xl bg-surface border border-line tnum" />
          <p className="text-dim text-sm mt-2">
            Only the day changes. The amount stays the same and the guest's balance
            will not move — if it would, the change is refused.
          </p>

          <button disabled={busy || !movingCharge.date}
            onClick={async () => {
              setBusy(true)
              try {
                await moveRoomChargeDate(movingCharge.id, movingCharge.date)
                toast('Charge moved to ' + movingCharge.date, 'success')
                setMovingCharge(null); refresh(); onChanged?.()
              } catch (e) { toast(e.message, 'error') }
              setBusy(false)
            }}
            className="mt-6 w-full h-14 rounded-2xl bg-amber text-bg text-lg font-bold disabled:opacity-40">
            {busy ? 'Moving…' : 'Move it'}
          </button>
          <button onClick={() => setMovingCharge(null)}
            className="mt-2 w-full h-12 text-dim">Cancel</button>
        </div>
      </div>
    )}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="text-dim">{label}</span>
      <span className="tnum">{naira(value)}</span>

    </div>
  )
}
